import { sharedAppSurface } from "../../utils/analytics/app-sharing";
import { retainServerSourceReceipt } from "../../pages/home-page/anonymous-upload-claims";
import { hasActivePaidEntitlement, subscriptionTimestampMilliseconds } from "../../utils/subscription-entitlements";
import { getSubscriptionBillingIdentity } from "../../utils/subscription-billing";
import { createSubscriptionRequestAuthority, SubscriptionRequestCancelledError } from "../../utils/subscription-request-authority";
import { getSubscriptionPurchaseRoute, SubscriptionPurchaseRoute } from "../../components/upsell/subscriptionPurchaseRoute";
import {
  ContentMediumType,
  DeckColors,
  DeckObject,
  UserEmailAndNotificationPreferences,
  GetPageFromUrlErrorTypes,
  NUM_OF_FREE_AI_IMAGE_OCCLUSIONS_ALLOWED,
  getInitialMonthlyGenerationsLeft,
  CardData,
  SubscriptionStatus,
  UserData,
  UserSubscriptionPlan,
  UserType,
  JUNGLE_BACKEND_PROD_DOMAIN,
  WISDOLIA_LANDING_PAGE_DOMAIN,
  CardType,
  MAX_NUMBER_OF_REFERRED_USERS_WITH_INCENTIVE,
  NUM_OF_ADDITIONAL_GENERATION_CARDS_BY_REFERRAL,
  UNDERSTANDING_RATING_FOR_GETTING_MCQ_CORRECT,
  UNDERSTANDING_RATING_FOR_GETTING_FREE_RESPONSE_AND_CASE_SCENARIO_CORRECT,
  EventSentToMobileApp,
  ManagerSubscriptionAccount,
  DECK_COLLECTION_NAME,
  CARDS_SAVED_COLLECTION_NAME,
  LEADERBOARD_COLLECTION_NAME,
  NUM_OF_ANSWER_FEEDBACKS_ALLOWED_PER_TIME_LIMIT,
  NUM_OF_CHAT_MESSAGES_ALLOWED_PER_TIME_LIMIT,
  NUM_OF_FREE_ADVANCED_QUESTION_GENERATIONS_ALLOWED,
  NUM_OF_FREE_TEMPLATED_QUESTION_GENERATIONS_ALLOWED,
  FirebaseDate,
  VIDEO_ID_TO_YOUTUBE_VIDEO_TRANSCRIPT_COLLECTION_NAME,
  FIREBASE_VAP_ID_KEY,
  StripeSubscriptionStatus,
  GRATITUDE_ENTRIES_BY_USER_COLLECTION_NAME,
  USERS_COLLECTION_NAME,
  EXAMS_COLLECTION_NAME,
  ExamObject,
  STREAKS_COLLECTION_NAME,
  UNDERSTANDING_RATING_FOR_GETTING_TRUE_OR_FALSE_QUESTION_CORRECT,
  QuestionDifficultyGroup,
  QuestionTaxonomy,
  AnimalCharacterWithAccessory,
  LevelForAmountOfCardsToGenerate,
  MAX_PDF_PAGES_ALLOWED_FOR_FREE,
  MAX_PDF_PAGES_ALLOWED_FOR_SUPER_LEARNER_PLAN,
  NewFeaturesForWhatsNewModal,
  JUNGLE_CLOUDFLARE_BASE_URL,
  CheckS3DocumentAvailabilityResult,
} from "../../constants/GlobalTypesAndEnumsAndConstants";
import { createUser, getUser, updateUser } from "../models/UserModel";
import { grantElitePlanIfUserIsCreator } from "../models/CreatorContentModel";
import {
  computeVoiceSecondsRemaining,
  computeVoiceQuotaResetsAtMs,
  FREE_WEEKLY_VOICE_SECONDS_FALLBACK,
} from "../../voice/core/voiceQuotaHelpers";
import { getOpenPanelClientForJungleWebApp } from "../../utils/analytics/openpanel-client";
import { getDeckQuestionGenerationTrackingProperties } from "../../utils/analytics/question-generation-tracking";
import { detectDeviceType, isIOS, isAndroid } from "./detect-device";
import {
  updateDoc,
  doc,
  getDoc,
  onSnapshot,
  Timestamp,
  collection,
  where,
  getDocs,
  query,
  orderBy,
  limit,
  documentId,
  startAfter,
  type QueryDocumentSnapshot,
} from "firebase/firestore";
import { db, firebaseConfig, messaging } from "./init-firebase";
import { ensureAnonymousReadSession } from "./anonymous-read-session";
import { getFirebaseAuthorizationHeaders } from "./firebase-auth-headers";
import { getSignedExtractedImageUrl } from "./extracted-image-urls";
import {
  loadAcquisitionAttributionAfterArrivalCapture,
  loadAcquisitionAttributionModule,
  loadNewUserSignupTrackingModule,
} from "../../utils/attribution-module-loaders";
import type { AcquisitionAttribution } from "../../utils/acquisition-attribution";
import { createDeck, getDeckById, joinClassForUserFunction } from "../models/DeckModel";
// Device identity and referrer decoding are shared with the marketing site (jungleai.com), which must not
// load this module; they live in small modules and are re-exported here for the app's existing imports.
import { detectOS, getOrCreateDeviceUID } from "./device-identity";
import { decryptText, getStoredReferrerUserId } from "./referrer-id";
import { cachedReferralCode, isShortReferralCode } from "./referral-code";
import axios from "axios";
import { updateSessionListener } from "../models/SessionListenerModel";
import { getFiniteReviewProgressScore } from "./review-progress-helpers";
import { noteReviewProgressChangedByAnswer } from "../../zustand/reviewProgressMarksStore";
import { getAssessmentUnitStimulusHtmlForExport, orderCardsByAssessmentUnit } from "../../utils/assessment-units";
import { buildUpdatedUserRatingsForCard } from "./assessment-unit-rating-persistence";

import {
  cardStore,
  generateCardsStore,
  navigationWithStatesStore,
  pageStore,
  useReviewDeckStore,
  useSubscriptionUpsellStore,
} from "../../zustand/globalStore";
import { calculateNextFrfsReviewForCard, getCardsByDeckId } from "../models/CardModel";
import { updateLeaderboardForUser, updateLeaderboardScoreForUser } from "../models/LeaderboardModel";
import { getISOWeek } from "date-fns";

import {
  MonkeyWithBananaSvg,
  MonkeyWithGlassesSvg,
  MonkeyWithNoAccessorySvg,
  CapybaraWithFruitSvg,
  CapybaraWithGlassesSvg,
  CapybaraWithNoAccessorySvg,
  SlothWithFruitSvg,
  SlothWithGlassesSvg,
  SlothWithNoAccessorySvg,
} from "../../images/characters/profile-images";
import { getToken } from "firebase/messaging";
import Cookies from "js-cookie";
import { getGoogleAnalyticsSessionCookieName } from "../../utils/analytics/google-analytics-cookie";
import { redactStudyGuideTelemetryUrls } from "../../utils/analytics/study-guide-url-privacy";
import { defaultGamificationProps } from "./gamification-helper-methods/progression-and-river-animation";
import DOMPurify from "dompurify";
import { getYoutubeVideoId, isYouTubeHostname } from "./youtube-url";
import { getContentMediumTypeFromFileNameOrPageUrl } from "./content-medium";
import {
  CONTENT_INGESTION_USER_MESSAGES,
  ContentIngestionError,
  ContentIngestionErrorCode,
  ContentIngestionStage,
  type ContentIngestionUserMessage,
  normalizeContentUrl,
  requestContentEndpoint,
} from "./content-ingestion-observability";
import { trackError } from "../../utils/analytics/track-error";
import { cardHasPastMistake } from "./mistake-card-filter";
import {
  claimThisDeviceAsAssignmentUnitForNewAccount,
  getFirstExperienceTestPersonProperties,
} from "../../first-experience-tests/firstExperienceTests";
import { getStoredSharerDevice } from "../../utils/sharer-device";
import { getMonthlyGenerationLimitForAccount } from "../../first-experience-tests/freeAllowanceTest";
import { noteAnswerInAnswerFirstReview } from "../../pages/review-deck-page/first-user-experience/answer-first/answerFirstReview";

export { getYoutubeVideoId } from "./youtube-url";
export { getContentMediumTypeFromFileNameOrPageUrl } from "./content-medium";
export { detectOS, getOrCreateDeviceUID, decryptText };

export async function identifyUserForAnalytics(user?: UserData) {
  // The per-ad attribution helpers load after startup (already loaded by then in practice). Without them (a
  // failed download) the person is still identified, only without the acquisition properties.
  const acquisitionAttributionHelpers = await loadAcquisitionAttributionModule().catch(() => null);
  try {
    const deviceType = detectDeviceType();

    let eventTrackingProperties = {
      platform: pageStore.getState().isRunningAsMobileApp ? "Mobile App" : "Web",
      deviceType: deviceType,
      operatingSystem: detectOS(),
    };

    let deviceId = getOrCreateDeviceUID();
    eventTrackingProperties["deviceId"] = deviceId;

    const identityUser = new Map<string, any>();
    const { userSubscriptionPlan, userNumOfMonthlyGenerationsLeft } = getUserSubscriptionPlanAndLimits(user);

    // user is a newUser if the account was created less than or equal to 3 days ago
    // Use defensive helper so partner-created users with missing `date_created`
    // do not blow up the analytics identify call.
    const currentDate = new Date();
    const userCreatedDate = getDateFromDatabaseField(user.date_created);
    const timeDifference = Math.abs(currentDate.getTime() - userCreatedDate.getTime());
    const differenceDays = Math.ceil(timeDifference / (1000 * 3600 * 24));

    let whereDidUserComeFrom = null;

    // Same precedence as the Worker's deriveWhereDidUserComeFrom (the payment event): a questionnaire answer
    // counts only when its exactSourceName is non-blank; otherwise the legacy ad and referral fields follow.
    const questionnaireSourceName = user?.how_did_user_find_us?.where_user_came_from_option?.exactSourceName;
    if (typeof questionnaireSourceName === "string" && questionnaireSourceName.trim()) {
      whereDidUserComeFrom = questionnaireSourceName.trim();
    } else if (user?.how_did_user_find_us?.user_referrer_id) {
      whereDidUserComeFrom = "referredByUser";
    } else if (user?.how_did_user_find_us?.did_user_come_from_meta_ad) {
      whereDidUserComeFrom = "meta";
    } else if (user?.how_did_user_find_us?.google_campaign_id) {
      whereDidUserComeFrom = "google";
    } else if (user?.how_did_user_find_us?.tiktok_campaign_id) {
      whereDidUserComeFrom = "tiktok";
    } else if (user?.how_did_user_find_us?.reddit_campaign_id) {
      whereDidUserComeFrom = "reddit";
    } else if (user?.how_did_user_find_us?.influencer_campaign_id) {
      whereDidUserComeFrom = "influencer";
    }
    // When the legacy fields give nothing, a Meta first touch (Meta ad id or Meta utm_source) reads as "meta",
    // as on the Worker's payment event (deriveWhereDidUserComeFrom).
    if (
      !whereDidUserComeFrom &&
      acquisitionAttributionHelpers?.isMetaAcquisitionTouch(user?.acquisition_attribution?.first_touch)
    ) {
      whereDidUserComeFrom = "meta";
    }
    identityUser.set("email", user?.email_auth);
    identityUser.set("universityId", user.university?.id);
    identityUser.set("universityName", user.university?.name);
    identityUser.set("userId", user.id);
    identityUser.set("userEmail", user.email_auth);
    identityUser.set("userName", user?.name);
    identityUser.set("userSubscriptionPlan", userSubscriptionPlan);
    identityUser.set("userType", user?.user_type ? user?.user_type : "unknown");
    identityUser.set("studentType", user?.student_type ? user?.student_type : "unknown");
    identityUser.set("numOfSetsOfCardsGenerated", user?.number_of_sets_of_cards_generated);
    identityUser.set("whereDidUserComeFrom", whereDidUserComeFrom);
    identityUser.set("userReferrerId", user?.how_did_user_find_us?.user_referrer_id);
    identityUser.set("userReferrerSharerDevice", user?.how_did_user_find_us?.user_referrer_sharer_device);
    identityUser.set("didUserComeFromMetaAd", user?.how_did_user_find_us?.did_user_come_from_meta_ad);
    identityUser.set("googleCampaignId", user?.how_did_user_find_us?.google_campaign_id);
    identityUser.set("tiktokCampaignId", user?.how_did_user_find_us?.tiktok_campaign_id);
    identityUser.set("influencerCampaignId", user?.how_did_user_find_us?.influencer_campaign_id);
    identityUser.set("redditCampaignId", user?.how_did_user_find_us?.reddit_campaign_id);
    identityUser.set("utm_source", user?.how_did_user_find_us?.utm_source);
    for (const [acquisitionPropertyName, acquisitionPropertyValue] of Object.entries(
      acquisitionAttributionHelpers?.buildAcquisitionAnalyticsProperties(user?.acquisition_attribution, user) ?? {}
    )) {
      identityUser.set(acquisitionPropertyName, acquisitionPropertyValue);
    }
    // The first-experience tests' sides this person was exposed to (`first_experience_test_<experiment id>`),
    // including on this device before they signed up: OpenPanel does not link a signed-out device's events
    // to the account, so the person record is what joins an exposure to the account's later events.
    for (const [testPropertyName, testVariant] of Object.entries(getFirstExperienceTestPersonProperties(user))) {
      identityUser.set(testPropertyName, testVariant);
    }
    identityUser.set("isUserNew-LessThanOrEqualTo3Days", differenceDays <= 3);
    identityUser.set(
      "userSubscriptionStatus",
      user?.subscription_information?.subscription_status
        ? user?.subscription_information?.subscription_status
        : "inactive"
    );
    if (
      userSubscriptionPlan !== UserSubscriptionPlan.SUPER_LEARNER &&
      userSubscriptionPlan !== UserSubscriptionPlan.ELITE
    ) {
      identityUser.set("userNumOfMonthlyGenerationsLeft", userNumOfMonthlyGenerationsLeft);
    }
    if (user?.subscription_information?.subscription_period_type) {
      identityUser.set("userSubscriptionPeriodType", user?.subscription_information?.subscription_period_type);
    }

    // gamification properties
    if (user?.gamification_properties) {
      identityUser.set("totalXpEarned", user?.gamification_properties?.total_xp_earned);
      identityUser.set("currentLevel", user?.gamification_properties?.current_level);
      identityUser.set("currentTreeXp", user?.gamification_properties?.current_tree_being_used);
      identityUser.set("unlockedTrees", user?.gamification_properties?.unlocked_trees);
      identityUser.set("numOfBuddiesUnlocked", user?.gamification_properties?.number_of_buddies_unlocked);
      identityUser.set("characterTeamSelected", user?.gamification_properties?.character_team_selected);
      identityUser.set(
        "characterWithAccessorySelected",
        user?.gamification_properties?.character_with_accessory_selected
      );
    }

    // OpenPanel's identify accepts firstName / lastName / email /
    // avatar as top-level fields plus an arbitrary properties bag — we map
    // user.name to firstName (a best-effort split would be brittle for
    // international names).
    //
    // Exclude the keys that we already pass as top-level fields below
    // (profileId, email, firstName) so the OpenPanel person record doesn't
    // end up with each value stored twice — once at the top level and
    // again inside `properties`. Without this filter, dashboard segments
    // that count distinct `properties.email` values would silently
    // double-count.
    const openPanelTopLevelIdentityFieldKeys = new Set(["userId", "email", "userEmail", "userName"]);
    const openPanelIdentityProperties: Record<string, any> = {};
    for (const [identityPropertyKey, identityPropertyValue] of identityUser) {
      if (
        identityPropertyValue !== undefined &&
        identityPropertyValue !== null &&
        !openPanelTopLevelIdentityFieldKeys.has(identityPropertyKey)
      ) {
        openPanelIdentityProperties[identityPropertyKey] = identityPropertyValue;
      }
    }
    getOpenPanelClientForJungleWebApp().identify({
      profileId: user.id,
      email: user.email_auth || undefined,
      firstName: user?.name || undefined,
      properties: openPanelIdentityProperties,
    });
  } catch (e) {
    console.log("error in tracking event: ", e);
  }
}
export async function trackEvent(
  eventName: string,
  additionalEventProperties: any,
  user?: UserData | null,
  currentReviewDeck?: DeckObject | null
) {
  try {
    if (eventName === "Referral Link Copied" && ["WhatsApp", "iMessage"].includes(additionalEventProperties?.shareType)) {
      void trackEvent("App Share Requested", { share_surface: "referral", method: additionalEventProperties.shareType === "WhatsApp" ? "whatsapp" : "sms" }, null, null);
    }
    // Undefined keeps the legacy implicit deck-context behavior. Explicit null
    // is an opt-out for event families whose schema forbids deck/card context.
    if (currentReviewDeck === undefined) {
      currentReviewDeck = useReviewDeckStore.getState().currentReviewDeck;
    }
    const deviceType = detectDeviceType();

    let eventTrackingProperties = {
      platform: pageStore.getState().isRunningAsMobileApp ? "Mobile App" : "Web",
      deviceType: deviceType,
      operatingSystem: detectOS(),
      dateTracked: new Date().toISOString(),
    };

    let deviceId = getOrCreateDeviceUID();
    eventTrackingProperties["deviceId"] = deviceId;

    if (user) {
      const { userSubscriptionPlan, userNumOfMonthlyGenerationsLeft } = getUserSubscriptionPlanAndLimits(user);

      // user is a newUser if the account was created less than or equal to 3 days ago
      // Use defensive helper so partner-created users with missing `date_created`
      // do not blow up trackEvent (which gets called from many React render paths).
      const currentDate = new Date();
      const userCreatedDate = getDateFromDatabaseField(user.date_created);
      const timeDifference = Math.abs(currentDate.getTime() - userCreatedDate.getTime());
      const differenceDays = Math.ceil(timeDifference / (1000 * 3600 * 24));

      // PII stays on the OpenPanel person record populated by
      // identifyUserForAnalytics instead of being duplicated on every event.
      eventTrackingProperties["userId"] = user?.id;
      eventTrackingProperties["userCreatedDate"] = user?.date_created;
      eventTrackingProperties["userType"] = user?.user_type;
      eventTrackingProperties["studentType"] = user?.student_type;
      eventTrackingProperties["userSubscriptionPlan"] = userSubscriptionPlan;
      eventTrackingProperties["numOfSetsOfCardsGenerated"] = user?.number_of_sets_of_cards_generated;
      eventTrackingProperties["userReferrerId"] = user?.how_did_user_find_us?.user_referrer_id;
      eventTrackingProperties["universityName"] = user.university?.name;
      eventTrackingProperties["utm_source"] = user?.how_did_user_find_us?.utm_source;

      if (
        userSubscriptionPlan !== UserSubscriptionPlan.SUPER_LEARNER &&
        userSubscriptionPlan !== UserSubscriptionPlan.ELITE
      ) {
        eventTrackingProperties["userNumOfMonthlyGenerationsLeft"] = userNumOfMonthlyGenerationsLeft;
      }
      eventTrackingProperties["isUserNew-LessThanOrEqualTo3Days"] = differenceDays <= 3;
    } else {
      // user is not logged in, let's check if they were referred by someone
      const encryptedReferrerUserIdFromLocalStorage = localStorage.getItem("ref_uid");

      if (encryptedReferrerUserIdFromLocalStorage && encryptedReferrerUserIdFromLocalStorage !== "null") {
        if (encryptedReferrerUserIdFromLocalStorage === "true") {
          eventTrackingProperties["userReferrerId"] = encryptedReferrerUserIdFromLocalStorage;
        } else {
          eventTrackingProperties["userReferrerId"] = await getStoredReferrerUserId();
        }
        const storedSharerDevice = getStoredSharerDevice();
        if (storedSharerDevice) eventTrackingProperties["sharerDevice"] = storedSharerDevice;
      }
    }
    eventTrackingProperties = {
      ...eventTrackingProperties,
      ...additionalEventProperties,
    };

    if (currentReviewDeck) {
      eventTrackingProperties["deckId"] = currentReviewDeck?.id;
      eventTrackingProperties["pageUrl"] = currentReviewDeck?.generation_metadata?.page_url;
      eventTrackingProperties["pageDomainName"] = currentReviewDeck?.generation_metadata?.page_domain;
      eventTrackingProperties["contentMediumType"] = currentReviewDeck?.generation_metadata?.content_medium_type;
      eventTrackingProperties = {
        ...eventTrackingProperties,
        ...getDeckQuestionGenerationTrackingProperties(currentReviewDeck, eventTrackingProperties),
      };
    }

    eventTrackingProperties = redactStudyGuideTelemetryUrls(eventTrackingProperties);

    // OpenPanel is a no-op in dev / preview builds and when its client ID is unset.
    getOpenPanelClientForJungleWebApp().track(eventName, eventTrackingProperties);
  } catch (e) {
    console.log("error in tracking event: ", e);
  }
}

function isLocalStorageAvailable() {
  var test = "test";
  try {
    localStorage.setItem(test, test);
    localStorage.removeItem(test);
    return true;
  } catch (e) {
    return false;
  }
}

export function doesLocalStorageWork() {
  if (isLocalStorageAvailable()) {
    try {
      // Write
      localStorage.setItem("testKey", "testValue");
      // Read
      var value = localStorage.getItem("testKey");
      // Check
      if (value === "testValue") {
        localStorage.removeItem("testKey");
        return true;
      } else {
        localStorage.removeItem("testKey");
        return false;
      }
    } catch (e) {
      return false;
    }
  } else {
    return false;
  }
}

// Helper function to loop over documents in a collection
// and id to the returned objects
export const arrayElementLooper = (snapshot: any) => {
  let data: any = [];
  snapshot.forEach((doc: any) => {
    data.push({
      ...doc.data(),
      id: doc.id,
    });
  });
  return data;
};

// helper function to return the search results in the correct order
export const userSearchElementLooper = (snapshot: any) => {
  let data: any = [];
  snapshot.forEach((doc: any) => {
    data.push({
      bio: doc.data().bio,
      id: doc.id,
      name: doc.data().name,
      profile_picture: doc.data().profile_picture,
      username: doc.data().username,
    });
  });
  return data;
};

export function randomIntFromInterval(min: number, max: number) {
  // min and max included
  return Math.floor(Math.random() * (max - min + 1) + min);
}

// Format a date to look like this: July 3rd, 10:00 am
export function formatDate(date: Date) {
  // Array with months
  const months = [
    "January",
    "February",
    "March",
    "April",
    "May",
    "June",
    "July",
    "August",
    "September",
    "October",
    "November",
    "December",
  ];

  // Get the month
  let month = months[date.getMonth()];

  // Get the day
  let day = date.getDate();

  // Add the ordinal suffix
  const dayString = day.toString() + getOrdinalSuffix(day);

  // Get the hours
  let hours = date.getHours();
  let ampm = hours >= 12 ? "pm" : "am";
  hours = hours % 12;
  hours = hours ? hours : 12; // the hour '0' should be '12'

  // Get the minutes
  let minutesInt = date.getMinutes();
  const minutesString = minutesInt < 10 ? "0" + minutesInt.toString() : minutesInt.toString();

  // Format the date
  return `${month} ${dayString}, ${hours}:${minutesString} ${ampm}`;
}

// Helper function to get the ordinal suffix
function getOrdinalSuffix(day: number) {
  let j = day % 10,
    k = day % 100;
  if (j === 1 && k !== 11) {
    return "st";
  }
  if (j === 2 && k !== 12) {
    return "nd";
  }
  if (j === 3 && k !== 13) {
    return "rd";
  }
  return "th";
}

export function transformPageTextIntoSentencesArray(pageText: any, pdfStartingPage: any) {
  const token = "UNIQUETOKEN";
  const maxLength = 300;

  // Wrap periods that shouldn't cause a split with the unique identifier.
  const tempText = pageText.map((text) => {
    // Replace all non-ascii characters with a space. for example: "Diseases�of�the�Adrenal�" is common in pdfs
    if (!text) {
      return "";
    }
    // text = text.replace(/[^\x00-\x7F\u4E00-\u9FA5]+/g, " ");
    return text.replace(/(Mr|Mrs|Dr|Ms|St|Lt|Jr)\./g, `$1${token}`);
  });
  let pageTextSentencesArray = tempText.map((text) => text.split(/\./g));

  pageTextSentencesArray = pageTextSentencesArray.map((sentences) =>
    sentences
      .map((sentence) => sentence.replace(new RegExp(token, "g"), ".").trim())
      .filter((sentence) => sentence.length > 0)
  );

  let transformedText = "";
  let trimmedPageTextSentencesArray = [];
  let sentenceIndex = 0;
  pageTextSentencesArray.forEach((sentences, pageIndex) => {
    sentences.forEach((sentence) => {
      const trimmedSentence = sentence.trim().replace(/(\r\n|\n|\r)/gm, "");
      // If the sentence is longer than maxLength, split it into chunks
      const chunks = trimmedSentence.match(new RegExp(".{1," + maxLength + "}", "g")) || [];
      chunks.forEach((chunk) => {
        transformedText += `${sentenceIndex}. ${chunk} \n`;
        trimmedPageTextSentencesArray.push({
          start: pageIndex + parseInt(pdfStartingPage ? pdfStartingPage : 0),
          text: chunk,
        });
        sentenceIndex++;
      });
    });
  });

  return { trimmedPageTextSentencesArray, transformedText };
}

export function RandomDeckColor() {
  const numOfDeckColors = Object.keys(DeckColors).length;
  return `color${randomIntFromInterval(1, numOfDeckColors)}`;
}

export async function getCustomTokenForUserId(userId?: string) {
  if (!userId) {
    return;
  }

  const firebaseAuthorizationHeaders = await getFirebaseAuthorizationHeaders();
  const customTokenResponse = await fetch(`${JUNGLE_CLOUDFLARE_BASE_URL}/users/get_firebase_custom_token`, {
    method: "POST",
    body: JSON.stringify({ user_id: userId }),
    headers: {
      "Content-Type": "application/json",
      ...firebaseAuthorizationHeaders,
    },
  });
  if (!customTokenResponse || !customTokenResponse.ok || customTokenResponse.status !== 200) {
    return null;
  }
  const customToken = await customTokenResponse.text();
  return customToken;
}

export const hasUserSubscribedBefore = (user: UserData): boolean => {
  // Checkout callers use this to choose billing management. An expired initial
  // payment is terminal in Stripe, so it must allow a fresh checkout instead.
  return getSubscriptionBillingIdentity(user?.subscription_information).hasRecognizedBillingBinding && (
    user?.subscription_information?.subscription_status === SubscriptionStatus.ACTIVE ||
    (user?.subscription_information?.stripe_subscription_status &&
      [
        StripeSubscriptionStatus.PAST_DUE,
        StripeSubscriptionStatus.UNPAID,
        StripeSubscriptionStatus.TRIALING,
        StripeSubscriptionStatus.INCOMPLETE,
        StripeSubscriptionStatus.ACTIVE,
      ].includes(user.subscription_information.stripe_subscription_status))
  );
};

//get url for stripe billing portal if user has a stripe customer_id can open portal directly
export async function getStripeManageAccountSessionForUser(user: any, shouldRedirectToUpdateView: boolean = false,
  isRequestCurrent: () => boolean = () => true) {
  const authority = createSubscriptionRequestAuthority(user, isRequestCurrent);
  try {
    authority.assertCurrent();
    const billing = getSubscriptionBillingIdentity(user?.subscription_information);
    if (!user?.id || !billing.hasRecognizedBillingBinding || billing.managerSubscriptionAccount !== ManagerSubscriptionAccount.Stripe) {
      throw new Error("A current Stripe subscription binding is required");
    }
    const fallbackStripeBillingPortalUrl = `https://billing.stripe.com/p/login/28o7uigNJ9Ww6J2000?prefilled_email=${user.email_auth}`;
    try {
      const firebaseAuthorizationHeaders = await getFirebaseAuthorizationHeaders();
      authority.assertCurrent();
      const customUrlSession = await fetch(`${JUNGLE_CLOUDFLARE_BASE_URL}/generate_stripe_billing_portal_session_url`, {
        method: "POST",
        body: JSON.stringify({ user_id: user.id, should_redirect_to_update_view: shouldRedirectToUpdateView }),
        headers: {
          "Content-Type": "application/json",
          ...firebaseAuthorizationHeaders,
        },
      });
      authority.assertCurrent();
      if (!customUrlSession || !customUrlSession.ok || customUrlSession.status !== 200) {
        return fallbackStripeBillingPortalUrl;
      }
      const url = await customUrlSession.text();
      authority.assertCurrent();
      return typeof url === "string" && url.trim() ? url : fallbackStripeBillingPortalUrl;
    } catch (error) {
      // A revoked caller cannot adopt the public fallback either.
      authority.assertCurrent();
      return fallbackStripeBillingPortalUrl;
    }
  } finally {
    authority.dispose();
  }
}

//get url for stripe billing portal if user has a stripe customer_id can open portal directly
export async function getJungleSubscriptionUpgradeUrl(user: any, contextForShowingSubscriptionUpsell: string,
  isRequestCurrent: () => boolean = () => true) {
  const authority = createSubscriptionRequestAuthority(user, isRequestCurrent);
  try {
    if (!authority.isCurrent()) return null;
    const { userSubscriptionPlan, managerSubscriptionAccount } = getUserSubscriptionPlanAndLimits(user);
    const { hasSubscriptionBinding, hasRecognizedBillingBinding } = getSubscriptionBillingIdentity(user?.subscription_information);
    if (hasSubscriptionBinding && !hasRecognizedBillingBinding) {
      showAlertForUnrecognizedSubscriptionBilling();
      return null;
    }
    const page = pageStore.getState();
    const isRunningAsMobileApp = Boolean(window?.ReactNativeWebView || page.isRunningAsMobileApp);
    const purchaseRoute = getSubscriptionPurchaseRoute({
      isRunningAsMobileApp,
      isAndroid: page.isAndroid || isAndroid(),
      isIOS: page.isIOS || isIOS(),
      currentCountryUserIsIn: page.currentCountryUserIsIn,
      currentUserSubscriptionPlan: userSubscriptionPlan,
      managerSubscriptionAccount,
      hasSubscriptionBinding,
      hasUserSubscribedBefore: hasUserSubscribedBefore(user),
    });
    if (purchaseRoute === SubscriptionPurchaseRoute.ManageWhereTheSubscriptionWasBought) {
      showAlertForUpgradeAccountOnAppleAndAndroid(managerSubscriptionAccount);
      return null;
    }
    if (isRunningAsMobileApp &&
      (purchaseRoute !== SubscriptionPurchaseRoute.JungleStripeCheckout || !hasUserSubscribedBefore(user))) {
      useSubscriptionUpsellStore.getState().showSubscriptionUpsellModal({
        subscriptionPlanToShow: userSubscriptionPlan === UserSubscriptionPlan.FREE
          ? UserSubscriptionPlan.MEGA_MIND : UserSubscriptionPlan.SUPER_LEARNER,
        contextForShowingSubscriptionUpsell,
      });
      return null;
    }

    if (managerSubscriptionAccount === ManagerSubscriptionAccount.Stripe && hasUserSubscribedBefore(user)) {
      const urlForManagingStripeSubscription = await getStripeManageAccountSessionForUser(user, true, authority.isCurrent);
      if (!authority.isCurrent()) return null;
      return urlForManagingStripeSubscription;
    }

    return `${WISDOLIA_LANDING_PAGE_DOMAIN}/upgrade`;
  } catch (error) {
    if (error instanceof SubscriptionRequestCancelledError) return null;
    throw error;
  } finally {
    authority.dispose();
  }
}

export function sleep(sleepCallback: any, time: number) {
  return new Promise((resolve) => {
    setTimeout(() => {
      sleepCallback();
      resolve(null);
    }, time);
  });
}

export async function getPageDataFromPdf(
  pageUrl: string,
  userId: string,
  contentMediumType: ContentMediumType,
  ingestionAttemptId?: string
) {
  try {
    const dataFromUrl = await requestContentEndpoint<any>({
      url: `${JUNGLE_BACKEND_PROD_DOMAIN}/file_or_url/get_pdf_data_from_url`,
      body: {
        supports_source_receipts: true,
        page_url: pageUrl,
        user_id: userId,
        content_medium_type: contentMediumType,
        ingestion_attempt_id: ingestionAttemptId,
      },
    });
    return { dataFromUrl: retainServerSourceReceipt(dataFromUrl, `${JUNGLE_BACKEND_PROD_DOMAIN}/file_or_url/create_task_to_get_file_data`) };
  } catch (error) {
    if (
      error instanceof ContentIngestionError &&
      error.httpStatus !== undefined &&
      [401, 403, 404, 410].includes(error.httpStatus)
    ) {
      return {
        errorMessage: CONTENT_INGESTION_USER_MESSAGES.pdfLinkUnavailable,
        sourceRejectionHttpStatus: error.httpStatus,
      };
    }
    if (error instanceof ContentIngestionError && error.httpStatus === 415) {
      return {
        errorMessage: CONTENT_INGESTION_USER_MESSAGES.pdfLinkNotDirectFile,
        sourceRejectionHttpStatus: error.httpStatus,
      };
    }
    throw error;
  }
}

export async function getPageDataFromWebpage(pageUrl: string, userId: string, ingestionAttemptId?: string) {
  try {
    const result = await requestContentEndpoint<any>({
      url: `${JUNGLE_BACKEND_PROD_DOMAIN}/file_or_url/get_page_text_from_page_url`,
      body: {
        supports_source_receipts: true,
        page_url: pageUrl,
        user_id: userId,
        ingestion_attempt_id: ingestionAttemptId,
      },
    });
    return result && typeof result === "object"
      ? retainServerSourceReceipt(result, `${JUNGLE_BACKEND_PROD_DOMAIN}/file_or_url/create_task_to_get_file_data`)
      : result;
  } catch (error) {
    if (!(error instanceof ContentIngestionError) || error.code !== ContentIngestionErrorCode.Http) {
      throw error;
    }

    const errorMessageFromRequest = (error.responseText || "").replaceAll('"', "").trim();
    let shouldShowChromeExtensionUpsell = false;
    let errorMessage: ContentIngestionUserMessage | null;

    if (errorMessageFromRequest.includes(GetPageFromUrlErrorTypes.DocumentIsPrivateGoogleDocs)) {
      const isGoogleSlidesDoc = pageUrl.includes("docs.google") && pageUrl.includes("presentation");
      errorMessage = isGoogleSlidesDoc
        ? CONTENT_INGESTION_USER_MESSAGES.privateGoogleSlidesPresentation
        : CONTENT_INGESTION_USER_MESSAGES.privateGoogleDocument;
    } else if (errorMessageFromRequest.includes(GetPageFromUrlErrorTypes.WebpageHasSecurityProblems)) {
      errorMessage = CONTENT_INGESTION_USER_MESSAGES.webpagePrivacyRestricted;
      shouldShowChromeExtensionUpsell = true;
    } else if (errorMessageFromRequest.includes(GetPageFromUrlErrorTypes.GoogleDriveSizeLimitExceeded)) {
      errorMessage = CONTENT_INGESTION_USER_MESSAGES.googleDriveFileTooLarge;
      shouldShowChromeExtensionUpsell = true;
    } else if (errorMessageFromRequest.includes("An error occurred: Failed to convert Google Doc to PDF")) {
      errorMessage = CONTENT_INGESTION_USER_MESSAGES.googleDocConversionFailed;
      shouldShowChromeExtensionUpsell = true;
    } else if (error.httpStatus === 403) {
      errorMessage = CONTENT_INGESTION_USER_MESSAGES.webpagePrivacyRestricted;
      shouldShowChromeExtensionUpsell = true;
    } else if (error.httpStatus !== undefined && [404, 410].includes(error.httpStatus)) {
      errorMessage = CONTENT_INGESTION_USER_MESSAGES.webpageUnavailable;
    } else if (error.httpStatus === 415) {
      // The backend sends free-form English copy for 415 with no stable code.
      errorMessage = errorMessageFromRequest ? { serverProvidedText: errorMessageFromRequest } : null;
    } else {
      // Unknown 5xx responses are service failures, not user/source
      // rejections. Preserve the typed HTTP error for Sentry and retry copy.
      throw error;
    }
    return {
      errorMessage,
      shouldShowChromeExtensionUpsell,
      sourceRejectionHttpStatus: error.httpStatus,
    };
  }
}

/**
 * Validates if an S3 document is available for download using the same backend
 * logic as PDFViewerComponent's handleDocumentLoadError / checkAndRestoreS3Document.
 * Returns whether the document is ready to download or an error message so the caller
 * can surface it through its own error handling UI.
 */
export async function checkS3DocumentAvailabilityForDownload(
  url: string,
  userId?: string
): Promise<CheckS3DocumentAvailabilityResult> {
  const maxAttempts = 2; // initial attempt + 1 retry

  for (let attempt = 1; attempt <= maxAttempts; attempt++) {
    try {
      const response = await fetch(`${JUNGLE_CLOUDFLARE_BASE_URL}/file_or_url/check_and_restore_s3_document`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          s3_url: url,
          user_id: userId,
        }),
      });

      if (!response.ok) {
        // Treat 5xx and other non-OK statuses as errors so we can retry once
        throw new Error(`Backend responded with status ${response.status}`);
      }

      const data = await response.json();

      if (!data || typeof data !== "object") {
        return {
          available: false,
          errorMessage: "Invalid response format",
          status: "error",
        };
      }
      if (
        data.status === "success" &&
        data.restoration_status === "not_needed" &&
        data.message?.toLowerCase().includes("document is available")
      ) {
        return {
          available: true,
          downloadUrl: url,
          status: "not_needed",
        };
      }

      if (data.status !== "success") {
        return {
          available: false,
          errorMessage: data.message || "Failed to check document status",
          status: "error",
        };
      }

      switch (data.restoration_status) {
        case "completed": {
          // Document was restored and should now be available. Use a cache-busting
          // URL similar to PDFViewerComponent so clients don't get stale 403s.
          const downloadUrl = `${url}${url.includes("?") ? "&" : "?"}t=${Date.now()}`;
          return {
            available: true,
            downloadUrl,
            estimatedHours: data.estimated_hours,
            estimatedCompletion: data.estimated_completion,
            status: "completed",
          };
        }
        case "not_needed":
          return {
            available: false,
            errorMessage:
              "Document is available but failed to load. The link may have expired or you may not have permission to access it.",
            status: "error",
          };
        case "in_progress":
        case "initiated":
          return {
            available: false,
            errorMessage: "Document recovery is in progress. Please try again later.",
            status: "in_progress",
            estimatedHours: data.estimated_hours,
            estimatedCompletion: data.estimated_completion,
          };
        default:
          return {
            available: false,
            errorMessage: data.message || "Document is not available for download",
            status: "error",
          };
      }
    } catch (error) {
      console.error(`Error checking S3 document availability (attempt ${attempt}/${maxAttempts}):`, error);
      if (attempt < maxAttempts) {
        await new Promise((resolve) => setTimeout(resolve, 500));
      } else {
        return {
          available: false,
          errorMessage: "Failed to check document status. Please try again later.",
          status: "error",
        };
      }
    }
  }

  return {
    available: false,
    errorMessage: "Failed to check document status. Please try again later.",
    status: "error",
  };
}

export async function getDataFromUrl(
  generatedDeck: DeckObject,
  user: UserData,
  setLoadingProgressPercentage?: (percentageToGenerateCards: number) => void,
  ingestionAttemptId?: string
) {
  if (setLoadingProgressPercentage) setLoadingProgressPercentage(20);
  let errorMessage: ContentIngestionUserMessage | null = null;
  let shouldShowChromeExtensionUpsell = false;
  let sourceRejectionHttpStatus: number | undefined;

  // Early validation checks
  if (generatedDeck?.generation_metadata?.page_url?.includes("file:///")) {
    errorMessage = CONTENT_INGESTION_USER_MESSAGES.localFileUrl;
    return {
      errorMessage,
      shouldShowChromeExtensionUpsell,
      sourceRejectionHttpStatus,
      newPageData: generatedDeck,
    };
  }

  generatedDeck.generation_metadata.page_url = normalizeContentUrl(generatedDeck.generation_metadata.page_url);
  const normalizedSourceUrl = new URL(generatedDeck.generation_metadata.page_url);
  if (isYouTubeHostname(normalizedSourceUrl.hostname) && !getYoutubeVideoId(normalizedSourceUrl.toString())) {
    throw new ContentIngestionError({
      message: "YouTube URL did not include a valid video ID",
      stage: ContentIngestionStage.ValidateUrl,
      code: ContentIngestionErrorCode.InvalidUrl,
      retryable: false,
    });
  }

  // Set content type and update metadata
  generatedDeck.generation_metadata.content_medium_type = getContentMediumTypeFromFileNameOrPageUrl(
    generatedDeck?.generation_metadata?.page_url
  );
  if (getYoutubeVideoId(generatedDeck.generation_metadata?.page_url)) {
    generatedDeck.generation_metadata.content_medium_type = ContentMediumType.YouTubeVideo;
  }
  generatedDeck.generation_metadata.page_domain = getPageDomainName(generatedDeck.generation_metadata?.page_url);

  if (
    [ContentMediumType.Audio, ContentMediumType.Video, ContentMediumType.AnkiTxt].includes(
      generatedDeck.generation_metadata.content_medium_type
    )
  ) {
    throw new ContentIngestionError({
      message: "This URL source type must be uploaded as a local file",
      stage: ContentIngestionStage.ValidateUrl,
      code: ContentIngestionErrorCode.SourceRejected,
      retryable: false,
      userMessage: CONTENT_INGESTION_USER_MESSAGES.uploadMediaFileDirectly,
    });
  }

  // Initialize parallel requests array
  const parallelRequests: Promise<any>[] = [];

  // Add content-specific requests
  let contentRequest;
  switch (generatedDeck.generation_metadata.content_medium_type) {
    case ContentMediumType.Image:
      {
        const imageUrl = new URL(generatedDeck.generation_metadata.page_url);
        const encodedImageFileName = imageUrl.pathname.split("/").filter(Boolean).pop();
        let imageDisplayName = imageUrl.hostname;
        if (encodedImageFileName) {
          try {
            imageDisplayName = decodeURIComponent(encodedImageFileName);
          } catch {
            imageDisplayName = encodedImageFileName;
          }
        }
        generatedDeck.generation_metadata.page_title = imageDisplayName;
      }
      generatedDeck.generation_metadata.pdf_images_object_list = [
        {
          imageUrl: generatedDeck.generation_metadata.page_url,
          fileName: generatedDeck.generation_metadata.page_title,
        },
      ];
      break;

    case ContentMediumType.Webpage:
      contentRequest = getPageDataFromWebpage(
        generatedDeck.generation_metadata.page_url,
        user?.id,
        ingestionAttemptId
      ).then((pageDataResponse) => {
        if (pageDataResponse?.errorMessage) {
          errorMessage = pageDataResponse.errorMessage;
          shouldShowChromeExtensionUpsell = pageDataResponse.shouldShowChromeExtensionUpsell;
          sourceRejectionHttpStatus = pageDataResponse.sourceRejectionHttpStatus;
        } else if (pageDataResponse) {
          const {
            page_title,
            page_text,
            pdf_file_name,
            trimmed_page_text_sentences_array,
            pdf_images_document_id,
            pdf_num_pages,
            is_pdf_file,
            page_url,
            uploaded_file_s3_object_key,
          } = pageDataResponse;

          generatedDeck.generation_metadata.page_title = page_title;
          generatedDeck.generation_metadata.study_guide_source_receipt_id = pageDataResponse.study_guide_source_receipt_id;
          generatedDeck.generation_metadata.page_text = page_text;
          if (page_url) {
            generatedDeck.generation_metadata.page_url = page_url;
          }
          if (uploaded_file_s3_object_key) {
            generatedDeck.generation_metadata.uploaded_file_s3_object_key = uploaded_file_s3_object_key;
          }
          if (is_pdf_file) {
            // Calculate max allowed pages based on user subscription
            let maxPdfPagesAllowed = MAX_PDF_PAGES_ALLOWED_FOR_FREE;
            if (user?.subscription_information?.subscription_status === SubscriptionStatus.ACTIVE) {
              maxPdfPagesAllowed = MAX_PDF_PAGES_ALLOWED_FOR_SUPER_LEARNER_PLAN;
            }
            // Set the default ending page to the minimum of pdf pages and allowed limit
            const defaultEndingPage = Math.min(pdf_num_pages, maxPdfPagesAllowed);
            Object.assign(generatedDeck.generation_metadata, {
              pdf_num_pages,
              pdf_pages_text_array: trimmed_page_text_sentences_array,
              pdf_images_object_list_doc_id: pdf_images_document_id,
              content_medium_type: ContentMediumType.PDF,
              pdf_file_name,
              pdf_url_for_file_upload: true,
              pdf_ending_page: defaultEndingPage,
              pdf_starting_page: 1,
            });
          } else {
            generatedDeck.generation_metadata.page_text_sentences_array = trimmed_page_text_sentences_array.map(
              (text: string) => ({ text, start: 0 })
            );
          }
        }
      });
      parallelRequests.push(contentRequest);
      break;

    case ContentMediumType.PDF:
      contentRequest = getPageDataFromPdf(
        generatedDeck.generation_metadata?.page_url,
        user?.id,
        generatedDeck.generation_metadata.content_medium_type,
        ingestionAttemptId
      ).then(({ dataFromUrl, errorMessage: pdfErrorMessage, sourceRejectionHttpStatus: pdfRejectionHttpStatus }) => {
        if (dataFromUrl) {
          const {
            pdfNumPages,
            pdfPagesTextArray,
            pdfDocumentName,
            pdfImagesDocumentId,
            page_url,
            uploaded_file_s3_object_key,
          } = dataFromUrl;
          const formattedFileName = pdfDocumentName.replace(/\s/g, "+");
          const maxPdfPagesAllowed =
            user?.subscription_information?.subscription_status === SubscriptionStatus.ACTIVE
              ? MAX_PDF_PAGES_ALLOWED_FOR_SUPER_LEARNER_PLAN
              : MAX_PDF_PAGES_ALLOWED_FOR_FREE;
          Object.assign(generatedDeck.generation_metadata, {
            study_guide_source_receipt_id: dataFromUrl.study_guide_source_receipt_id,
            pdf_num_pages: pdfNumPages,
            pdf_pages_text_array: pdfPagesTextArray,
            pdf_file_name: formattedFileName,
            page_title: formattedFileName,
            pdf_images_object_list_doc_id: pdfImagesDocumentId,
            page_url: page_url || generatedDeck?.generation_metadata?.page_url,
            uploaded_file_s3_object_key:
              uploaded_file_s3_object_key || generatedDeck?.generation_metadata?.uploaded_file_s3_object_key,
            pdf_starting_page: 1,
            pdf_ending_page: Math.min(pdfNumPages, maxPdfPagesAllowed),
          });
        } else {
          errorMessage = pdfErrorMessage;
          sourceRejectionHttpStatus = pdfRejectionHttpStatus;
        }
      });
      parallelRequests.push(contentRequest);
      break;

    case ContentMediumType.YouTubeVideo:
      contentRequest = getYouTubeTranscriptArrayAndNumOfMinutes(
        generatedDeck.generation_metadata.page_url,
        user,
        ingestionAttemptId
      ).then((data) => {
        const { youtubeTranscriptTextAndTimestampsArray, youtubeTranscriptNumMinutes, youtube_video_title, videoId } =
          data;
        generatedDeck.generation_metadata.video_or_audio_num_minutes = youtubeTranscriptNumMinutes;
        generatedDeck.generation_metadata.video_or_audio_starting_minute = 0;
        generatedDeck.generation_metadata.video_or_audio_ending_minute = youtubeTranscriptNumMinutes;
        generatedDeck.generation_metadata.video_transcript_text_and_timestamps_array =
          youtubeTranscriptTextAndTimestampsArray;
        generatedDeck.generation_metadata.page_title = youtube_video_title;
        generatedDeck.generation_metadata.page_url = `https://youtube.com/watch?v=${videoId}`;
      });
      parallelRequests.push(contentRequest);
      break;
  }

  // Wait for all requests to complete
  if (setLoadingProgressPercentage) setLoadingProgressPercentage(40);
  await Promise.all(parallelRequests);
  if (setLoadingProgressPercentage) setLoadingProgressPercentage(80);

  // Final validation and cleanup
  if (
    generatedDeck.generation_metadata.content_medium_type === ContentMediumType.Webpage &&
    !generatedDeck.generation_metadata.page_text
  ) {
    return {
      errorMessage: errorMessage || CONTENT_INGESTION_USER_MESSAGES.webpageWithoutReadableContent,
      shouldShowChromeExtensionUpsell,
      sourceRejectionHttpStatus,
    };
  }

  if (generatedDeck?.generation_metadata?.page_text) {
    generatedDeck.generation_metadata.page_text = generatedDeck.generation_metadata.page_text.replace(
      /(\r\n|\n|\r)/gm,
      ""
    );
  }

  if (
    generatedDeck.generation_metadata.pdf_images_object_list &&
    generatedDeck?.generation_metadata?.content_medium_type !== ContentMediumType.Image
  ) {
    generatedDeck.generation_metadata.pdf_images_object_list = null;
  }

  return {
    errorMessage,
    shouldShowChromeExtensionUpsell,
    sourceRejectionHttpStatus,
    newPageData: generatedDeck,
  };
}

/**
 * Basic cookie reader – your original function was already fine 👍
 */
export function getCookie(name: string): string | null {
  const decoded = decodeURIComponent(document.cookie);
  return (
    decoded
      .split(";")
      .map((c) => c.trim())
      .find((c) => c.startsWith(name + "="))
      ?.split("=")[1] ?? null
  );
}

/**
 * Build a properly‑formatted _fbc value if one isn't in cookies yet but
 * the fbclid URL param is available.
 *
 * Format: fb.<subdomainIndex>.<timestamp>.<fbclid>
 * - subdomainIndex: 1 when you're setting a first‑party cookie yourself
 * - timestamp: ms since epoch
 */
function buildFbcFromUrl(): string | null {
  const params = new URLSearchParams(window.location.search);
  const fbclid = params.get("fbclid");
  if (!fbclid) return null;
  const timestamp = Date.now();
  return `fb.1.${timestamp}.${fbclid}`;
}

// get tiktok tracking cookie ids
export function getTiktokTrackingCookieId() {
  let ttp = getCookie("_ttp");

  if (!ttp) {
    const constructed = buildFbcFromUrl();
    if (constructed) {
      // Store it as a first‑party cookie so subsequent hits have it
      document.cookie = `_ttp=${constructed}; path=/; expires=${new Date(
        Date.now() + 1000 * 60 * 60 * 24 * 730 // 2 years
      ).toUTCString()}; SameSite=Lax`;
      ttp = constructed;
    }
  }

  return { ttp };
}

export async function methodAfterCreatingUser(
  user: any,
  userAuth: any,
  userReferrerId: string | null,
  didUserComeFromMetaAd: boolean | null,
  googleCampaignId: string | null,
  tiktokCampaignId: string | null,
  tiktokClickId: string | null,
  redditCampaignId: string | null,
  influencerCampaignId: string | null,
  newsletterCampaignId: string | null,
  idOfAmbassadorThatReferredUser: string | null,
  signUpMethod: string,
  userName: string | null
) {
  // The signup event and the ad platforms' signup conversions (Meta CompleteRegistration first) load after
  // startup (new-user-signup-tracking.ts). The account setup below (starter deck, referral credit) never waits
  // for or depends on that download.
  void loadNewUserSignupTrackingModule()
    .then(({ trackNewUserSignUp }) =>
      trackNewUserSignUp({
        user,
        signUpMethod,
        userReferrerId,
        didUserComeFromMetaAd,
        googleCampaignId,
        tiktokCampaignId,
        tiktokClickId,
        redditCampaignId,
        influencerCampaignId,
        newsletterCampaignId,
        idOfAmbassadorThatReferredUser,
      })
    )
    .catch((error) => {
      trackError(error, { scope: "new-user-signup-tracking", userId: user?.id, level: "warning" });
    });

  try {
    // create deck named Jungle
    let deckToSave: DeckObject = {
      name: "Jungle",
      is_private: false,
      date_created: new Date(),
      color: RandomDeckColor(),
      is_active: true,
      cards_count: 0,
      user_id: user?.id,
    };
    await createDeck(deckToSave, user);

    // Award the referral bonus on the referrer's account, via the Cloudflare worker.
    // Why not write to the referrer's Firestore doc directly from the client?
    //   The new user's browser doesn't own the referrer's user doc, so under standard Firestore
    //   security rules a cross-user write fails silently — that's been the source of the
    //   "I referred a friend but didn't get my generations" reports. The CF endpoint runs with
    //   service-account creds and applies the credit atomically + idempotently.
    if (userReferrerId) {
      getUser(userReferrerId).then((userThatDidTheReferral: UserData) => {
        trackEvent(
          "Referral: User referred another user and they signed up",
          { userIdForUserThatWasReferred: user.id },
          userThatDidTheReferral
        );
      });

      fetch(`${JUNGLE_CLOUDFLARE_BASE_URL}/users/credit_referral_bonus`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          new_user_id: user.id,
          referrer_user_id: userReferrerId,
        }),
      })
        .then(async (response) => {
          const responseBody = await response.json().catch(() => null);
          if (!response.ok) {
            // The CF endpoint either rejected the request (bad input) or hit an internal error.
            // Track loudly — this is exactly the kind of silent failure we built this fix to surface.
            trackEvent(
              "Referral: Failed to credit referrer with referral bonus",
              {
                referrerUserId: userReferrerId,
                userIdForUserThatWasReferred: user.id,
                httpStatus: response.status,
                responseBody,
              },
              user
            );
            return;
          }
          // Successful HTTP response — the CF endpoint may have credited the bonus, or skipped it for
          // a legitimate reason (paid plan, cap reached, etc.). Track each outcome distinctly so we can
          // monitor the funnel: how many referrals fire vs how many actually get credited.
          const referralCreditStatus = responseBody?.result?.status ?? "unknown";
          trackEvent(
            "Referral: Credit referrer with referral bonus completed",
            {
              referrerUserId: userReferrerId,
              userIdForUserThatWasReferred: user.id,
              referralCreditStatus,
              generationsAwarded: responseBody?.result?.generationsAwarded ?? null,
              newReferralCount: responseBody?.result?.newReferralCount ?? null,
            },
            user
          );
        })
        .catch((error) => {
          trackEvent(
            "Referral: Failed to credit referrer with referral bonus",
            {
              referrerUserId: userReferrerId,
              userIdForUserThatWasReferred: user.id,
              errorMessage: error?.message,
              errorName: error?.name,
            },
            user
          );
        });
    }
  } catch (error) {
    console.log("Error: Failed to do tracking for new user sign up", error);
    trackEvent(
      "Error: Failed to do tracking for new user sign up",
      { error, errorMessage: error?.message },
      user
    );
  }
}

const accountIdsWhoseMissingCountersWereSeededThisPageLoad = new Set<string>();

export function getUserSubscriptionPlanAndLimits(user: UserData | null) {
  // Billing ownership survives paid expiry. Older Stripe profiles omit the
  // manager; infer it only from an actual binding, retaining explicit providers.
  const { managerSubscriptionAccount } = getSubscriptionBillingIdentity(user?.subscription_information);

  let number_of_referred_users = user?.subscription_information?.number_of_referred_users
    ? user?.subscription_information?.number_of_referred_users
    : 0;

  // The country's allowance, or everyone's on the first-experience test T5b's "two_generations" side (here,
  // what pages show, a side forced for QA counts too; the monthly reset, which writes, ignores it)
  const baseGenerationLimit = getMonthlyGenerationLimitForAccount(user, { includeSideForcedForQa: true });
  let userNumOfMonthlyGenerationsAllowed =
    baseGenerationLimit + number_of_referred_users * NUM_OF_ADDITIONAL_GENERATION_CARDS_BY_REFERRAL;
  if (number_of_referred_users <= MAX_NUMBER_OF_REFERRED_USERS_WITH_INCENTIVE) {
    userNumOfMonthlyGenerationsAllowed =
      baseGenerationLimit + number_of_referred_users * NUM_OF_ADDITIONAL_GENERATION_CARDS_BY_REFERRAL;
  }

  // TODO: delete later
  const userNumOfAnswerFeedbacksRemaining =
    user?.subscription_information?.num_of_answer_feedbacks_remaining !== undefined
      ? user?.subscription_information?.num_of_answer_feedbacks_remaining
      : NUM_OF_ANSWER_FEEDBACKS_ALLOWED_PER_TIME_LIMIT;

  const userNumOfChatMessagesRemaining =
    user?.subscription_information?.num_of_chat_messages_remaining !== undefined
      ? user?.subscription_information?.num_of_chat_messages_remaining
      : NUM_OF_CHAT_MESSAGES_ALLOWED_PER_TIME_LIMIT;

  const userNumOfMonthlyGenerationsLeft =
    user?.subscription_information?.num_of_monthly_generations_left !== undefined
      ? user?.subscription_information?.num_of_monthly_generations_left
      : baseGenerationLimit;

  let userNumOfAdvancedQuestionGenerationsLeft =
    user?.subscription_information?.num_of_advanced_question_generations_left;

  // Templated (e.g. USMLE Step 1) question generations — 1/month for every plan
  // below Elite; Elite is unlimited (call sites check plan === ELITE before this
  // counter). Lazily initialized like the advanced-questions counter, in one write.
  let userNumOfTemplatedQuestionGenerationsLeft =
    user?.subscription_information?.num_of_templated_question_generations_left;

  if (userNumOfAdvancedQuestionGenerationsLeft == null || userNumOfTemplatedQuestionGenerationsLeft == null) {
    const missingCounterFieldsToSeed: Record<string, number> = {};
    if (userNumOfAdvancedQuestionGenerationsLeft == null) {
      userNumOfAdvancedQuestionGenerationsLeft = NUM_OF_FREE_ADVANCED_QUESTION_GENERATIONS_ALLOWED;
      missingCounterFieldsToSeed.num_of_advanced_question_generations_left =
        NUM_OF_FREE_ADVANCED_QUESTION_GENERATIONS_ALLOWED;
    }
    if (userNumOfTemplatedQuestionGenerationsLeft == null) {
      userNumOfTemplatedQuestionGenerationsLeft = NUM_OF_FREE_TEMPLATED_QUESTION_GENERATIONS_ALLOWED;
      missingCounterFieldsToSeed.num_of_templated_question_generations_left =
        NUM_OF_FREE_TEMPLATED_QUESTION_GENERATIONS_ALLOWED;
    }

    // The write carries only the seeded counters (updateUser merges maps), and goes out once per account per
    // page load. This runs on every render with whatever user object the caller holds, and a new account's
    // first seconds are full of objects from before the seed arrived: spreading such an object's whole
    // subscription_information wrote its old values back, among them the free generations that AuthContext
    // had just lowered to 1 in the one-generation countries, or that a first generation had just spent.
    if (user?.id && !accountIdsWhoseMissingCountersWereSeededThisPageLoad.has(user.id)) {
      accountIdsWhoseMissingCountersWereSeededThisPageLoad.add(user.id);
      updateUser(user.id, { subscription_information: missingCounterFieldsToSeed });
    }
  }

  let userNumOfImageOcclusionUsesLeft =
    user?.subscription_information?.num_of_ai_image_occlusions_left !== undefined
      ? user?.subscription_information?.num_of_ai_image_occlusions_left
      : NUM_OF_FREE_AI_IMAGE_OCCLUSIONS_ALLOWED;

  const userNumOfMonthlyLiveRecordingsLeft =
    user?.subscription_information?.num_of_monthly_live_recordings_left !== undefined
      ? user?.subscription_information?.num_of_monthly_live_recordings_left
      : 3; // Default to 3 for free users

  // Billing-date display remains independent of the canonical entitlement status.
  const subscriptionPeriodEnd = subscriptionTimestampMilliseconds(user?.subscription_information?.current_period_end_date);
  const hasSubscriptionPeriodEnded = user ? subscriptionPeriodEnd !== null && subscriptionPeriodEnd <= Date.now() : true;

  // Voice tutor weekly free quota. Only Elite is unlimited; Free + Pro share
  // the cap. The server owns the counter; these are derived for display + the
  // client cap timer.
  const voiceQuotaInformation = user?.subscription_information?.ai_voice_quota_information;
  const nowMsForVoiceQuota = new Date().getTime();
  const userVoiceFreeSecondsPerWeek =
    voiceQuotaInformation?.free_seconds_per_week ?? FREE_WEEKLY_VOICE_SECONDS_FALLBACK;
  const userVoiceSecondsRemaining = computeVoiceSecondsRemaining(voiceQuotaInformation, nowMsForVoiceQuota);
  const userVoiceQuotaResetsAtMs = computeVoiceQuotaResetsAtMs(voiceQuotaInformation, nowMsForVoiceQuota);
  const userVoiceFreeLimitDepletedCount = voiceQuotaInformation?.lifetime_depleted_count ?? 0;

  if (
    !hasActivePaidEntitlement(user?.subscription_information?.subscription_status, user?.subscription_information?.subscription_plan_type, {
      subscription: user?.subscription_information,
    })
  ) {
    return {
      userSubscriptionPlan: UserSubscriptionPlan.FREE,
      hasSubscriptionPeriodEnded: hasSubscriptionPeriodEnded,
      userNumOfAnswerFeedbacksRemaining: userNumOfAnswerFeedbacksRemaining,
      userNumOfChatMessagesRemaining: userNumOfChatMessagesRemaining,
      userNumOfMonthlyGenerationsAllowed: userNumOfMonthlyGenerationsAllowed,
      userNumOfMonthlyGenerationsLeft: userNumOfMonthlyGenerationsLeft,
      userNumOfAdvancedQuestionGenerationsLeft: userNumOfAdvancedQuestionGenerationsLeft,
      userNumOfTemplatedQuestionGenerationsLeft: userNumOfTemplatedQuestionGenerationsLeft,
      userNumOfImageOcclusionUsesLeft: userNumOfImageOcclusionUsesLeft,
      userNumOfMonthlyLiveRecordingsLeft: userNumOfMonthlyLiveRecordingsLeft,
      numberOfUserReferrals: number_of_referred_users,
      managerSubscriptionAccount,
      subscriptionPeriodType: null,
      userIsVoiceUnlimited: false,
      userVoiceFreeSecondsPerWeek: userVoiceFreeSecondsPerWeek,
      userVoiceSecondsRemaining: userVoiceSecondsRemaining,
      userVoiceQuotaResetsAtMs: userVoiceQuotaResetsAtMs,
      userVoiceFreeLimitDepletedCount: userVoiceFreeLimitDepletedCount,
    };
  }
  // Image occlusion is an Elite entitlement (2026-06-09): plans below Elite —
  // including Pro/Super Learner — share the same metered free quota. Elite
  // call sites gate on plan === ELITE and never read this counter. MegaMind is
  // deprecated and auto-migrated to Super Learner on auth load (AuthContext),
  // so no MegaMind-specific default is needed.
  userNumOfImageOcclusionUsesLeft =
    user?.subscription_information?.num_of_ai_image_occlusions_left !== undefined
      ? user?.subscription_information?.num_of_ai_image_occlusions_left
      : NUM_OF_FREE_AI_IMAGE_OCCLUSIONS_ALLOWED;

  // NEW — Elite plan branch. Entitlements are identical to Super Learner for v1 (PR 1).
  // Feature gates specific to Elite (voice mode, Step 1 templates, better AI model) will
  // check userSubscriptionPlan === ELITE in follow-up PRs — they are NOT handled here.
  if (user?.subscription_information?.subscription_plan_type === UserSubscriptionPlan.ELITE) {
    return {
      userSubscriptionPlan: UserSubscriptionPlan.ELITE,
      hasSubscriptionPeriodEnded: hasSubscriptionPeriodEnded,
      userNumOfAnswerFeedbacksRemaining: userNumOfAnswerFeedbacksRemaining,
      userNumOfChatMessagesRemaining: userNumOfChatMessagesRemaining,
      userNumOfMonthlyGenerationsAllowed: userNumOfMonthlyGenerationsAllowed,
      userNumOfMonthlyGenerationsLeft: userNumOfMonthlyGenerationsLeft,
      userNumOfAdvancedQuestionGenerationsLeft: userNumOfAdvancedQuestionGenerationsLeft,
      userNumOfTemplatedQuestionGenerationsLeft: userNumOfTemplatedQuestionGenerationsLeft,
      userNumOfImageOcclusionUsesLeft: userNumOfImageOcclusionUsesLeft,
      userNumOfMonthlyLiveRecordingsLeft: userNumOfMonthlyLiveRecordingsLeft,
      numberOfUserReferrals: number_of_referred_users,
      managerSubscriptionAccount,
      subscriptionPeriodType: user?.subscription_information?.subscription_period_type,
      userIsVoiceUnlimited: true,
      userVoiceFreeSecondsPerWeek: userVoiceFreeSecondsPerWeek,
      userVoiceSecondsRemaining: userVoiceSecondsRemaining,
      userVoiceQuotaResetsAtMs: userVoiceQuotaResetsAtMs,
      userVoiceFreeLimitDepletedCount: userVoiceFreeLimitDepletedCount,
    };
  }

  // right now, the only possible thing it could be if it gets to this point is the super learner plan
  return {
    userSubscriptionPlan: UserSubscriptionPlan.SUPER_LEARNER,
    hasSubscriptionPeriodEnded: hasSubscriptionPeriodEnded,
    userNumOfAnswerFeedbacksRemaining: userNumOfAnswerFeedbacksRemaining,
    userNumOfChatMessagesRemaining: userNumOfChatMessagesRemaining,
    userNumOfMonthlyGenerationsAllowed: userNumOfMonthlyGenerationsAllowed,
    userNumOfMonthlyGenerationsLeft: userNumOfMonthlyGenerationsLeft,
    userNumOfAdvancedQuestionGenerationsLeft: userNumOfAdvancedQuestionGenerationsLeft,
    userNumOfTemplatedQuestionGenerationsLeft: userNumOfTemplatedQuestionGenerationsLeft,
    userNumOfImageOcclusionUsesLeft: userNumOfImageOcclusionUsesLeft,
    userNumOfMonthlyLiveRecordingsLeft: userNumOfMonthlyLiveRecordingsLeft,
    numberOfUserReferrals: number_of_referred_users,
    managerSubscriptionAccount,
    subscriptionPeriodType: user?.subscription_information?.subscription_period_type,
    userIsVoiceUnlimited: false,
    userVoiceFreeSecondsPerWeek: userVoiceFreeSecondsPerWeek,
    userVoiceSecondsRemaining: userVoiceSecondsRemaining,
    userVoiceQuotaResetsAtMs: userVoiceQuotaResetsAtMs,
    userVoiceFreeLimitDepletedCount: userVoiceFreeLimitDepletedCount,
  };
}

export function getMonthlyGenerationResetDate(user: UserData, now: Date = new Date()): Date | null {
  const dateNow = new Date(now.getTime());
  const previousReset = user.subscription_information?.last_date_monthly_generations_were_reset;
  let lastDateMonthlyGenerationsWereReset: Date;
  if (previousReset) {
    // The due check runs before deciding whether auth must await maintenance.
    // Clone dates so this read cannot mutate the subscribed profile or its clock.
    lastDateMonthlyGenerationsWereReset = new Date(getDateFromDatabaseField(previousReset).getTime());
  } else {
    // Preserve the existing missing-reset fallback and one-month catch-up step.
    lastDateMonthlyGenerationsWereReset = new Date(now.getTime());
    lastDateMonthlyGenerationsWereReset.setMonth(lastDateMonthlyGenerationsWereReset.getMonth() - 2);
  }
  dateNow.setHours(0, 0, 0, 0);
  lastDateMonthlyGenerationsWereReset.setHours(0, 0, 0, 0);
  const oneMonthAgoFromToday = new Date(dateNow.setMonth(dateNow.getMonth() - 1));
  if (!(lastDateMonthlyGenerationsWereReset <= oneMonthAgoFromToday)) return null;
  lastDateMonthlyGenerationsWereReset.setMonth(lastDateMonthlyGenerationsWereReset.getMonth() + 1);
  return lastDateMonthlyGenerationsWereReset;
}

export async function checkAndUpdateUserGenerationLimits(
  user: UserData,
  numOfAdvancedQuestionGenerationsAllowed: number = NUM_OF_FREE_ADVANCED_QUESTION_GENERATIONS_ALLOWED
) {
  const monthlyGenerationsResetDate = getMonthlyGenerationResetDate(user);
  if (monthlyGenerationsResetDate) {
    // reset monthly subscription limit. Only the reset counters are written (updateUser merges maps): copying
    // the whole subscription_information of this snapshot would also write back its other, possibly stale,
    // values.
    const updatedSubscriptionInformation: any = {};
    const numberOfReferredUsers = user.subscription_information?.number_of_referred_users;

    const aditionalGenerations =
      NUM_OF_ADDITIONAL_GENERATION_CARDS_BY_REFERRAL *
      (numberOfReferredUsers
        ? numberOfReferredUsers <= MAX_NUMBER_OF_REFERRED_USERS_WITH_INCENTIVE
          ? numberOfReferredUsers
          : MAX_NUMBER_OF_REFERRED_USERS_WITH_INCENTIVE
        : 0);
    updatedSubscriptionInformation.num_of_ai_image_occlusions_left = NUM_OF_FREE_AI_IMAGE_OCCLUSIONS_ALLOWED;
    updatedSubscriptionInformation.num_of_advanced_question_generations_left = numOfAdvancedQuestionGenerationsAllowed;
    updatedSubscriptionInformation.num_of_templated_question_generations_left =
      NUM_OF_FREE_TEMPLATED_QUESTION_GENERATIONS_ALLOWED;

    // MegaMind is deprecated — getUserSubscriptionPlanAndLimits never derives it
    // (raw MegaMind plan types fall into the Super Learner branch) and AuthContext
    // migrates the stored plan type on every auth load, so no MegaMind-specific
    // monthly allowance is needed here.
    updatedSubscriptionInformation.num_of_monthly_generations_left =
      getMonthlyGenerationLimitForAccount(user) + aditionalGenerations;

    updatedSubscriptionInformation.last_date_monthly_generations_were_reset = monthlyGenerationsResetDate;

    updateUser(user.id, {
      subscription_information: updatedSubscriptionInformation,
    });
  }
}

/**
 * The `acquisition_attribution` of a profile created now, once this page's arrival touch is stored (App.tsx).
 * A failed download of the attribution helpers loses the attribution, never the account.
 */
async function readAcquisitionAttributionForNewUser(userId: string): Promise<AcquisitionAttribution | null> {
  try {
    const isSigningUpInNativeApp = Boolean(window?.ReactNativeWebView || pageStore.getState().isRunningAsMobileApp);
    const { buildAcquisitionAttributionForNewUser } = await loadAcquisitionAttributionAfterArrivalCapture();
    return buildAcquisitionAttributionForNewUser({
      signupPlatform: isSigningUpInNativeApp ? "mobile_app" : "web",
      nativeInstallAttribution: window.__JUNGLE_NATIVE_INSTALL_ATTRIBUTION__,
    });
  } catch (error) {
    trackError(error, { scope: "new-user-acquisition-attribution", userId, level: "warning" });
    return null;
  }
}

export async function createUserOnFirebase(
  userAuth: any,
  userReferrerId: string | null,
  tiktokCampaignId: string | null,
  tiktokClickId: string | null,
  didUserComeFromMetaAd: boolean | null,
  googleCampaignId: string | null,
  redditCampaignId: string | null,
  influencerCampaignId: string | null,
  newsletterCampaignId: string | null,
  idOfAmbassadorThatReferredUser: string | null,
  numOfAdvancedQuestionGenerationsAllowed: number = NUM_OF_FREE_ADVANCED_QUESTION_GENERATIONS_ALLOWED
) {
  if (!userAuth || (userAuth && !userAuth.uid)) {
    return null;
  }

  const existingUser = await getUser(userAuth.uid);
  if (existingUser) {
    if (!userAuth.email) {
      trackEvent("Error: User Auth Email Not Found", {});
    }
    return existingUser;
  }

  // Never put short-code network availability on the profile-creation critical path.
  // Persist unresolved signup attribution on this account; authenticated background work
  // later resolves it and uses the existing atomic reward ledger, even after a reload.
  const storedCode = localStorage.getItem("ref_uid");
  const pendingReferralCode = storedCode && isShortReferralCode(storedCode) ? storedCode : null;
  if (pendingReferralCode) {
    userReferrerId = decryptText(pendingReferralCode) || null;
  } else {
    try {
      const storedReferrer = await getStoredReferrerUserId();
      if (storedReferrer && storedReferrer !== "true") userReferrerId = storedReferrer;
    } catch { /* Optional attribution must never prevent account creation. */ }
  }
  if (userReferrerId === userAuth.uid || userReferrerId === "true") userReferrerId = null;

  let signUpMethod: "Email" | "Microsoft" | "Apple" | "Google" = "Email";
  try {
    let howDidUserFindOutAboutUs = null;
    if (userReferrerId) {
      // The device the referrer shared the link from, kept with the referrer for every later report on this account.
      const sharerDeviceOfReferrer = getStoredSharerDevice();
      howDidUserFindOutAboutUs = {
        user_referrer_id: userReferrerId,
        ...(sharerDeviceOfReferrer ? { user_referrer_sharer_device: sharerDeviceOfReferrer } : {}),
      };
    } else if (didUserComeFromMetaAd) {
      howDidUserFindOutAboutUs = { did_user_come_from_meta_ad: didUserComeFromMetaAd };
    } else if (googleCampaignId) {
      howDidUserFindOutAboutUs = { google_campaign_id: googleCampaignId };
    } else if (tiktokCampaignId) {
      howDidUserFindOutAboutUs = { tiktok_campaign_id: tiktokClickId };
    } else if (redditCampaignId) {
      howDidUserFindOutAboutUs = { reddit_campaign_id: redditCampaignId };
    } else if (influencerCampaignId) {
      howDidUserFindOutAboutUs = { influencer_campaign_id: influencerCampaignId };
    } else if (newsletterCampaignId) {
      howDidUserFindOutAboutUs = { newsletter_campaign_id: newsletterCampaignId };
    } else if (idOfAmbassadorThatReferredUser) {
      howDidUserFindOutAboutUs = { id_of_ambassador_that_referred_user: idOfAmbassadorThatReferredUser };
    }

    if (localStorage.getItem("utm_source")) {
      howDidUserFindOutAboutUs = howDidUserFindOutAboutUs || {};
      howDidUserFindOutAboutUs.utm_source = localStorage.getItem("utm_source");
    }

    // Per-ad attribution (contract C2): the first and last campaign touches this browser recorded.
    const acquisitionAttribution = await readAcquisitionAttributionForNewUser(userAuth.uid);

    let userName = userAuth?.displayName;
    if (!userName) {
      userName = userAuth?.reloadUserInfo?.displayName;
    }

    function getClientId(gaCookieValue: string) {
      if (!gaCookieValue) return null;
      const parts = gaCookieValue.split(".");
      if (parts.length < 4) return null;
      return `${parts[2]}.${parts[3]}`;
    }

    function getSessionId(gaMeasurementIdCookieValue: string) {
      if (!gaMeasurementIdCookieValue) return null;
      const parts = gaMeasurementIdCookieValue.split(".");
      if (parts.length < 4) return null;
      return `${parts[2]}`;
    }

    const gaCookieValue = Cookies.get("_ga");
    const googleAnalyticsSessionCookieName = getGoogleAnalyticsSessionCookieName(firebaseConfig.measurementId);
    const gaMeasurementIdCookieValue = googleAnalyticsSessionCookieName
      ? Cookies.get(googleAnalyticsSessionCookieName)
      : undefined;
    const googleAnalyticsClientId = getClientId(gaCookieValue);
    const googleAnalyticsSessionId = getSessionId(gaMeasurementIdCookieValue);

    const providerId = userAuth?.providerData[0]?.providerId;
    switch (providerId) {
      case "microsoft.com":
        signUpMethod = "Microsoft";
        break;
      case "apple.com":
        signUpMethod = "Apple";
        break;
      case "google.com":
        signUpMethod = "Google";
        break;
      default:
        signUpMethod = "Email";
    }

    // If the user generated cards anonymously on the homepage before signing up, that generation
    // never got charged against any account (the backend decrement is gated on user_id, which was
    // null for anonymous requests). Deduct it from their starting allowance so the first generation
    // counts whether it happened before or after signup.
    const haveCardsBeenGeneratedAnonymouslyBeforeSignup =
      typeof window !== "undefined" && !!localStorage.getItem("haveCardsBeenGeneratedBefore");
    const initialNumOfMonthlyGenerationsLeft = getInitialMonthlyGenerationsLeft(
      undefined,
      haveCardsBeenGeneratedAnonymouslyBeforeSignup
    );

    // The first-experience tests draw a signed-out visitor's sides from this device's id; the account keeps
    // drawing from it, so a visitor keeps their sides through sign up and on every device. A second account made
    // in this browser gets none and draws from its uid, its own (firstExperienceTests.ts).
    const experimentAssignmentUnitId = claimThisDeviceAsAssignmentUnitForNewAccount(userAuth.uid);

    const userDataForNewUser: UserData = {
      ...(pendingReferralCode ? { pending_referral_code: pendingReferralCode } : {}),
      ...(experimentAssignmentUnitId ? { experiment_assignment_unit_id: experimentAssignmentUnitId } : {}),
      email_auth: userAuth.email,
      email_communication: userAuth.email,
      authentication_provider: signUpMethod,
      name: userName ? userName : null,
      date_created: new Date(),
      date_of_last_visit: new Date(),
      subscription_information: {
        num_of_answer_feedbacks_remaining: NUM_OF_ANSWER_FEEDBACKS_ALLOWED_PER_TIME_LIMIT,
        num_of_chat_messages_remaining: NUM_OF_CHAT_MESSAGES_ALLOWED_PER_TIME_LIMIT,
        num_of_monthly_generations_left: initialNumOfMonthlyGenerationsLeft,
        num_of_advanced_question_generations_left: numOfAdvancedQuestionGenerationsAllowed,
        last_date_monthly_generations_were_reset: new Date(),
        subscription_status: null,
        subscription_plan_type: UserSubscriptionPlan.FREE,
        number_of_referred_users: 0,
        num_of_ai_image_occlusions_left: 1,
      },
      user_type: null,
      student_type: null,
      how_did_user_find_us: howDidUserFindOutAboutUs,
      acquisition_attribution: acquisitionAttribution,
      save_card_default_option: "Jungle",
      gamification_properties: defaultGamificationProps(),
      user_settings: {
        should_include_card_url_source_when_saving_to_anki: false,
        email_notification_preferences: [
          UserEmailAndNotificationPreferences.productUpdateEmails,
          UserEmailAndNotificationPreferences.weeklyReviewEmails,
          UserEmailAndNotificationPreferences.reviewReminderEmails,
        ],
        notification_preferences: {
          review_reminder_on_mobile: false, // Set this to true once the user allows push notifications
          review_reminder_on_desktop: false, // Set this to true once the user allows desktop notifications
        },
        question_types_user_selected_to_generate: [
          { cardType: CardType.RegularMultipleChoiceQuestion, difficultyGroup: QuestionDifficultyGroup.Basic },
          { cardType: CardType.RegularFreeResponseQuestion, difficultyGroup: QuestionDifficultyGroup.Basic },
          { cardType: CardType.TrueOrFalseQuestion, difficultyGroup: QuestionDifficultyGroup.Basic },
          { cardType: CardType.RegularMultipleChoiceQuestion, difficultyGroup: QuestionDifficultyGroup.Advanced },
          { cardType: CardType.RegularFreeResponseQuestion, difficultyGroup: QuestionDifficultyGroup.Advanced },
          { cardType: CardType.CaseScenarioMultipleChoiceQuestion, difficultyGroup: QuestionDifficultyGroup.Advanced },
          { cardType: CardType.CaseScenarioFreeResponseQuestion, difficultyGroup: QuestionDifficultyGroup.Advanced },
        ],
      },
      number_of_sets_of_cards_generated: 0,
      user_referrer_id: userReferrerId,
      features_user_has_seen_in_whats_new_list: [
        NewFeaturesForWhatsNewModal.BadgesAndTimeCapsuleLetterFeature,
        NewFeaturesForWhatsNewModal.LeaderboardLeagues,
      ],
    };

    if (googleAnalyticsClientId && googleAnalyticsSessionId) {
      userDataForNewUser.tracking_data = {
        google_analytics_client_id: googleAnalyticsClientId,
        google_analytics_session_id: googleAnalyticsSessionId,
      };
    }

    // create user in Firebase
    const user = await createUser(userAuth.uid, userDataForNewUser);
    try {
      const windowType: any = window;
      windowType.dataLayer = windowType.dataLayer || [];
      windowType.dataLayer.push({ event: "sign_up" });
      windowType.dataLayer.push({ event: "tiktok_sign_up" });
    } catch (error) {
      trackError(error, {
        scope: "new-user-signup-data-layer",
        userId: userAuth.uid,
        level: "warning",
      });
    }
    void methodAfterCreatingUser(
      user,
      userAuth,
      userReferrerId,
      didUserComeFromMetaAd,
      googleCampaignId,
      tiktokCampaignId,
      tiktokClickId,
      redditCampaignId,
      influencerCampaignId,
      newsletterCampaignId,
      idOfAmbassadorThatReferredUser,
      signUpMethod,
      userName
    ).catch((error) => {
      trackError(error, {
        scope: "new-user-post-signup-work",
        userId: userAuth.uid,
        level: "warning",
      });
    });

    // Creators are comped on Elite. This optional grant must not turn a
    // successfully written user profile into an account-setup failure.
    try {
      await grantElitePlanIfUserIsCreator(userAuth.uid);
    } catch (error) {
      trackError(error, {
        scope: "new-user-creator-plan-grant",
        userId: userAuth.uid,
        level: "warning",
      });
    }
    return user;
  } catch (error) {
    trackError(error, {
      scope: "new-user-profile-creation",
      userId: userAuth.uid,
      tags: { authenticationProvider: signUpMethod },
    });
    throw error;
  }
}

export const getYouTubeTranscriptArrayAndNumOfMinutes = async (
  pageUrl: string,
  user: any,
  ingestionAttemptId?: string
): Promise<any> => {
  const videoId = getYoutubeVideoId(pageUrl);
  if (!videoId) {
    throw new ContentIngestionError({
      message: "YouTube URL did not include a valid video ID",
      stage: ContentIngestionStage.ValidateUrl,
      code: ContentIngestionErrorCode.InvalidUrl,
      retryable: false,
    });
  }

  // Transcript listeners and chunk reads require the same fresh Firebase
  // session as upload processing, including for a visitor's first YouTube link.
  if (!(await ensureAnonymousReadSession())) {
    throw new ContentIngestionError({
      message: "YouTube transcript read session is unavailable",
      stage: ContentIngestionStage.AwaitYouTubeTranscript,
      code: ContentIngestionErrorCode.Listener,
      retryable: true,
      userMessage: CONTENT_INGESTION_USER_MESSAGES.youtubeTranscriptListenerFailed,
    });
  }

  await requestContentEndpoint<{ status: string }>({
    url: `${JUNGLE_BACKEND_PROD_DOMAIN}/file_or_url/get_youtube_transcript_object`,
    body: {
      video_id: videoId,
      user_id: user?.id,
      device_id: getOrCreateDeviceUID(),
      ingestion_attempt_id: ingestionAttemptId,
    },
    stage: ContentIngestionStage.RequestYouTubeTranscript,
  });

  const youtubeVideoTranscriptDataDocRef = doc(db, VIDEO_ID_TO_YOUTUBE_VIDEO_TRANSCRIPT_COLLECTION_NAME, videoId);
  const { waitForYouTubeTranscriptSnapshot, normalizeYouTubeTranscriptWaitError } = await import(
    "./youtube-transcript-subscription"
  );
  try {
    return await waitForYouTubeTranscriptSnapshot({
      videoId,
      fetchLatestSnapshot: async () => {
        const { fetchLatestYouTubeTranscriptSnapshot } = await import("./youtube-transcript-storage");
        return fetchLatestYouTubeTranscriptSnapshot(videoId);
      },
      subscribe: (handleSnapshot, handleError) =>
        onSnapshot(youtubeVideoTranscriptDataDocRef, handleSnapshot, handleError),
      setIsGeneratingTranscript: (isGeneratingTranscript) =>
        generateCardsStore.getState().setIsGeneratingYoutubeVideoTranscript(isGeneratingTranscript),
    });
  } catch (error) {
    throw normalizeYouTubeTranscriptWaitError(error);
  }
};

export function shuffleArray(array: CardData[]) {
  for (let i = array.length - 1; i > 0; i--) {
    // Generate random number between 0 and i
    const j = Math.floor(Math.random() * (i + 1));

    // Swap elements array[i] and array[j]
    [array[i], array[j]] = [array[j], array[i]];
  }

  // do that the first 5 cards are always card_type = "Multiple Choice Question"
  let firstFiveCards = [];
  for (let card of array) {
    if (!card) continue;
    if (card.card_type === CardType.RegularMultipleChoiceQuestion || card.card_type === CardType.USMLEStep1MCQ) {
      firstFiveCards.push(card);
      //remove the card from the array
      array = array.filter((c) => c !== card);
      if (firstFiveCards.length === 5) {
        break;
      }
    }
  }
  // concat the first 5 cards with the rest of the cards
  return firstFiveCards.concat(array);
}
export function getPageDomainName(url: string) {
  // Check if the URL starts with 'http://' or 'https://'. If not, append 'http://'.
  if (!/^https?:\/\//i.test(url)) {
    url = "http://" + url;
  }

  // Create an anchor element to utilize its properties for parsing the URL
  let anchor = document.createElement("a");
  anchor.href = url;

  // Split hostname into parts
  let parts = anchor.hostname.split(".");

  // If the hostname has subdomains like "www" or "blog", etc.
  if (parts.length > 2) {
    // Generally, 'www' is a subdomain, so we'd remove it
    // but this check won't be accurate for domain names like 'example.co.uk'
    // so we can add more advanced checks if needed
    if (parts[0] === "www") {
      parts.shift(); // remove the first item
    }
  }

  return parts.join(".");
}

export function secondsToMinutes(seconds: number) {
  if (seconds <= 0) {
    seconds = 0;
  }
  const minutes: number = Math.floor(seconds / 60);
  return `${minutes}`;
}
export const exportCardsToAnkiFile = async (cards: Array<CardData>, deckName: string, user: any, source?: string) => {
  trackEvent("Exported Deck to Anki", { deckName: deckName, source }, user);
  const generateAnkiFileResponse: any = await fetch(`${JUNGLE_BACKEND_PROD_DOMAIN}/generate_anki_apkg_file`, {
    method: "POST",
    body: JSON.stringify({
      cards: orderCardsByAssessmentUnit(cards).map((card) => {
        return {
          case_scenario_details: getAssessmentUnitStimulusHtmlForExport(card),
          question: card?.question,
          answer: card?.answer,
          question_side_image_url: card?.question_side_image_url,
          answer_side_image_url: card?.answer_side_image_url,
          image_url_to_export_to_anki: card?.image_url_to_export_to_anki,
          distractor_answers_for_multiple_choice_question: card?.distractor_answers_for_multiple_choice_question,
        };
      }),
      deck_name: deckName,
    }),
    headers: {
      "Content-Type": "application/json",
    },
  }).catch((error) => {
    trackEvent("Error: Failed to get Anki File", { deckName: deckName }, user);
    // console.log(error);
  });
  if (generateAnkiFileResponse?.status === 200) {
    const blobAnkiFile = await generateAnkiFileResponse.blob();
    // If Jungle is running as a mobile app
    if (window?.ReactNativeWebView) {
      const blobAnkiFileReader = new FileReader();
      blobAnkiFileReader.onloadend = () => {
        let fileToSave = {
          blobUrl: blobAnkiFileReader.result,
          fileName: `${deckName}.apkg`,
        };
        window.ReactNativeWebView.postMessage(
          JSON.stringify({
            typeEvent: EventSentToMobileApp.DOWNLOAD_FILE,
            message: JSON.stringify(fileToSave),
          })
        );
      };
      blobAnkiFileReader.readAsDataURL(blobAnkiFile);
      return;
    }

    const blobAnkiUrl = window.URL.createObjectURL(blobAnkiFile);
    const linkElementToDownload = document.createElement("a");
    linkElementToDownload.href = blobAnkiUrl;
    linkElementToDownload.download = `${deckName}.apkg`;
    linkElementToDownload.click();
    window.URL.revokeObjectURL(blobAnkiUrl);
  }
};

export const openNewUrlPath = (url: string) => {
  if (!url) return;
  if (window?.ReactNativeWebView) {
    window.ReactNativeWebView.postMessage(
      JSON.stringify({
        typeEvent: EventSentToMobileApp.OPEN_URL,
        message: url,
      })
    );
    return;
  }
  const newWindow = window.open(url, "_blank");
  if (newWindow) {
    newWindow.focus();
  } else {
    // load in same window
    window.location.href = url;
  }
};

export const showAlertForUpgradeAccountOnWebsite = () => {
  alert(
    "To upgrade or manage your subscription, please visit jungleai.com. after logging in and going to settings, you can update your plan and review subscription details."
  );
};

export const showAlertForUpgradeAccountOnAppleAndAndroid = (managerSubscriptionAccount) => {
  alert(
    `Your subscription is managed by ${managerSubscriptionAccount}. To make changes, go to the Subscriptions section on your ${managerSubscriptionAccount} device.`
  );
};

export const showAlertForUnrecognizedSubscriptionBilling = () => {
  alert("We couldn't verify where this subscription is managed. Contact support before starting another subscription.");
};

// *********** Spaced Repetition helper methods ***********
// *********** Spaced Repetition helper methods ***********

// if the user presses "continue reviewing" we will create a new review
export async function createNewSpacedRepetitionReviewAfterUserChoseToContinueReviewing(
  userId: string,
  currentSpacedRepetitionReviewId: string
) {
  try {
    const response = await fetch(
      `${JUNGLE_CLOUDFLARE_BASE_URL}/spaced_repetition/create_new_spaced_repetition_review_for_user`,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          user_id: userId,
          did_user_request_to_continue_reviewing_cards: true,
          current_spaced_repetition_review_id: currentSpacedRepetitionReviewId,
        }),
      }
    );

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    return await response.json();
  } catch (error) {
    // console.error("There was a problem with the fetch operation: " + error.message);
    return null;
  }
}

export const encryptText = (textToEncrypt: string) => {
  const shortCode = cachedReferralCode(textToEncrypt);
  if (shortCode) return shortCode;
  const alphanumericChars = "0123456789ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz";
  const maxIndex = alphanumericChars.length;

  let encryptedText = textToEncrypt
    .split("")
    .map((char, index) => {
      let charIndex = alphanumericChars.indexOf(char);
      if (charIndex === -1) {
        // Character not alphanumeric
        return char;
      }

      if (index < 5) {
        charIndex = (charIndex + 3) % maxIndex;
      } else if (index >= 5 && index < 10) {
        charIndex = (charIndex + 1) % maxIndex;
      } else {
        charIndex = (charIndex - 2 + maxIndex) % maxIndex;
      }
      return alphanumericChars[charIndex];
    })
    .join("");

  if (textToEncrypt.length >= 7) {
    const seventhCharIndex = alphanumericChars.indexOf(textToEncrypt[6]);
    const extraChar1 = alphanumericChars[(seventhCharIndex + 6) % maxIndex];
    encryptedText += extraChar1;
  }

  if (textToEncrypt.length >= 4) {
    const fourthCharIndex = alphanumericChars.indexOf(textToEncrypt[3]);
    const extraChar2 = alphanumericChars[(fourthCharIndex - 3 + maxIndex) % maxIndex];
    encryptedText += extraChar2;
  }

  return encryptedText;
};

export const runPendingGenerationsForLearningJourney = async (
  deckData: DeckObject,
  user: UserData | null,
  isUserAFreeUser: boolean,
  module_name?: string,
  level_for_amount_of_cards_to_generate?: LevelForAmountOfCardsToGenerate
) => {
  let deviceId = getOrCreateDeviceUID();
  fetch(`${JUNGLE_BACKEND_PROD_DOMAIN}/generate_content/run_pending_generations_for_learning_journey`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
    },
    body: JSON.stringify({
      user_id: user?.id,
      module_name: module_name,
      deck_id: deckData?.id,
      session_id: deviceId,
      platform: pageStore.getState().isRunningAsMobileApp ? "Mobile App" : "Web",
      level_for_amount_of_cards_to_generate: level_for_amount_of_cards_to_generate,
      is_user_a_free_user: isUserAFreeUser,
    }),
  }).catch((error) => {
    console.error("Error: Failed to run pending generations | ", error);
    trackEvent(
      "Error: Failed to run pending generations",
      {
        errorMessage: error,
        deck_generation_id: deckData?.id,
      },
      user,
      deckData
    );
  });
};

export const orderCardsByCaseScenario = (cardsData: CardData[]) => {
  cardsData.sort((a, b) => {
    if (a.case_scenario_details < b.case_scenario_details) {
      return -1;
    } else if (a.case_scenario_details > b.case_scenario_details) {
      return 1;
    } else {
      return a.card_index - b.card_index;
    }
  });
  return cardsData;
};

export const groupCardsByCaseScenarioAndCardType = (cardsData: CardData[], cardType: CardType) => {
  return cardsData
    ?.filter((card) => card.card_type === cardType)
    .reduce((groupedCards, card) => {
      const key = card.case_scenario_details;
      groupedCards[key] = (groupedCards[key] || []).concat(card);
      return groupedCards;
    }, {});
};

export const groupCardsByCaseScenario = (cardsData: CardData[]) => {
  return cardsData?.reduce((groupedCards, card) => {
    const key = card.case_scenario_details;
    groupedCards[key] = (groupedCards[key] || []).concat(card);
    return groupedCards;
  }, {});
};

export const groupCardsByImageOcclusionURL = (cardsData: CardData[]) => {
  return cardsData?.reduce((groupedCards, card) => {
    const key = card.question_side_image_url;
    groupedCards[key] = (groupedCards[key] || []).concat(card);
    return groupedCards;
  }, {});
};

export const sendDataToExtension = async (userId: string) => {
  const firebaseAuthorizationHeaders = await getFirebaseAuthorizationHeaders();
  const customTokenResponse = await fetch(`${JUNGLE_CLOUDFLARE_BASE_URL}/users/get_firebase_custom_token`, {
    method: "POST",
    body: JSON.stringify({ user_id: userId }),
    headers: {
      "Content-Type": "application/json",
      ...firebaseAuthorizationHeaders,
    },
  });
  if (!customTokenResponse || !customTokenResponse.ok || customTokenResponse.status !== 200) {
    return;
  }
  const customToken = await customTokenResponse.text();
  let sessionId = sessionStorage.getItem("sesion_token_user");
  if (sessionId) {
    updateSessionListener(sessionId, {
      user_id: userId,
      session_id: customToken,
    });
  }
};

export type LocationData = {
  ipAddress?: string;
  country: string;
};

export const getLocationData = async (): Promise<LocationData> => {
  // Try multiple methods in sequence until we get a valid result
  try {
    // 1. Try IP-based location services first (fastest and most reliable)
    const ipBasedLocation = await getLocationDataByMultipleIPServices();
    if (ipBasedLocation) return ipBasedLocation;

    // 2. Fall back to geolocation API if IP services fail
    const geoLocation = await getLocationDataByGeoLocation();
    if (geoLocation) return geoLocation;

    // 3. If all methods fail, return null values
    return { country: null, ipAddress: null };
  } catch (error) {
    console.error("All location detection methods failed:", error);
    return { country: null, ipAddress: null };
  }
};

const getLocationDataByMultipleIPServices = async (): Promise<LocationData> => {
  const apiEndpoints = [
    {
      url: "https://ipinfo.io/json",
      extractData: (data: any) => ({
        country: data?.country,
        ipAddress: data.ip,
      }),
    },
    {
      url: "https://ipapi.co/json/",
      extractData: (data: any) => ({
        country: data.country_code,
        ipAddress: data.ip,
      }),
    },
    {
      url: "https://ipwhois.app/json/",
      extractData: (data: any) => ({
        country: data.country_code,
        ipAddress: data.ip,
      }),
    },
    // Backup method using separate API calls
    {
      url: "https://api.ipify.org?format=json",
      extractData: async (data: any) => {
        const ipAddress = data.ip;
        const countryResponse = await axios.get(`https://ipapi.co/${ipAddress}/country/`);
        return {
          country: countryResponse.data,
          ipAddress,
        };
      },
    },
  ];

  for (const endpoint of apiEndpoints) {
    try {
      const response = await fetch(endpoint.url);
      if (!response.ok) continue;

      const data = await response.json();
      const locationData = await endpoint.extractData(data);

      if (locationData?.country) {
        return locationData;
      }
    } catch (error) {
      console.warn(`Failed to fetch location from ${endpoint.url}, trying next service...`);
    }
  }

  throw new Error("All IP-based location services failed");
};

const getLocationDataByGeoLocation = (): Promise<LocationData> => {
  return new Promise((resolve, reject) => {
    if (!navigator.geolocation) {
      reject(new Error("Geolocation is not supported by this browser"));
      return;
    }

    navigator.geolocation.getCurrentPosition(
      async (position) => {
        try {
          const { latitude, longitude } = position.coords;
          const response = await axios.get(
            `https://api.bigdatacloud.net/data/reverse-geocode-client?latitude=${latitude}&longitude=${longitude}&localityLanguage=en`
          );

          resolve({
            country: response.data.countryCode,
            ipAddress: null, // Geolocation doesn't provide IP
          });
        } catch (error) {
          reject(new Error("Failed to get country from coordinates"));
        }
      },
      (error) => {
        reject(new Error("Failed to get geolocation"));
      },
      {
        timeout: 5000,
        maximumAge: 300000, // 5 minutes cache
      }
    );
  });
};

/** The readable part of a URL made from a document's name ("the-human-heart"); "" without a name. */
export function getDescriptiveNameSlug(deckName: string | null, maxLength = 20) {
  if (!deckName) return "";

  // Normalize string to remove accents and diacritics
  let normalizedDeckName = deckName.normalize("NFD").replace(/[\u0300-\u036f]/g, "");

  return normalizedDeckName
    .replace(/[^\p{L}\p{N}\s-]/gu, "") // Remove invalid characters
    .replace(/[?&!+$,;=@%()[\]{}\\^|~]/g, "") // Remove additional invalid characters
    .replace(/\s+/g, "-") // Replace multiple spaces with a single hyphen
    .toLowerCase()
    .substring(0, maxLength);
}

export function getDescriptiveUrlSlug(deckName: string | null) {
  if (!deckName) return "";
  return getDescriptiveNameSlug(deckName) + "-flashcards-multiple-choice-questions-quiz";
}

export const dateFormatWithSuffixDayNumber = (date: Date): string => {
  // Prefer the browser's first-choice locale → fall back to the runtime default → finally "en-US".
  const locale =
    (typeof navigator !== "undefined" && (navigator.languages?.[0] || navigator.language)) ||
    Intl.DateTimeFormat().resolvedOptions().locale ||
    "en-US";

  // "numeric", not "2-digit": a padded day read "November 02nd"
  const options: Intl.DateTimeFormatOptions = { month: "long", day: "numeric", year: "numeric" };
  const formattedDate = date.toLocaleDateString(locale, options);

  // Append an ordinal suffix only for English locales.
  if (/^en(-|$)/i.test(locale)) {
    const dayOfMonth = date.getDate();
    const suffix =
      dayOfMonth === 1 || dayOfMonth === 21 || dayOfMonth === 31
        ? "st"
        : dayOfMonth === 2 || dayOfMonth === 22
          ? "nd"
          : dayOfMonth === 3 || dayOfMonth === 23
            ? "rd"
            : "th";

    return formattedDate.replace(/\b\d{1,2}\b/, `$&${suffix}`);
  }

  return formattedDate;
};

export const wasUserCreatedLessThanOneHourAgo = (user): boolean => {
  const createdDate = user.date_created
    ? isFirebaseDate(user.date_created)
      ? new Date(user.date_created.seconds * 1000 + user.date_created.nanoseconds / 1000000)
      : user.date_created
    : new Date(0);
  const currentDate = new Date();
  const differenceInMilliseconds = currentDate.getTime() - createdDate.getTime();
  const differenceInHours = differenceInMilliseconds / (1000 * 60 * 60);
  return differenceInHours < 1;
};

export const joinClassForUser = async (deck: DeckObject, user: UserData, setCurrentReviewDeck: any) => {
  // get root deck, In case this deck is a subdeck, because the class will always be shared
  let rootDecks = deck?.parent_decks?.filter((parentDeck) => !parentDeck?.parent_deck_id);
  let rootDeckToJoin = rootDecks?.length > 0 ? rootDecks[0] : deck;
  await joinClassForUserFunction(rootDeckToJoin?.id, user?.id);
  deck?.class_properties?.users_in_class?.push(user);
  deck?.class_properties?.ids_of_users_in_class?.push(user?.id);
  setCurrentReviewDeck(deck);
};

export function updateCardStateAndDBWithNewAnswerAndRating(
  updateCardFromCardsDataState: any,
  currentCardData: CardData,
  user: UserData | undefined,
  knowledgeUnderstandingRatingNumber: number,
  userNumOfChatMessagesRemaining: number,
  userSubscriptionPlan: UserSubscriptionPlan,
  answerFromUser?: string,
  options?: { persistToDatabase?: boolean; ratedAt?: Date; persistNonAuthenticatedRating?: boolean }
) {
  const currentDate = options?.ratedAt || new Date();
  const ratingOwnerId = user?.id || "undefined";

  const isUserDoingDailyReview = window.location.pathname.includes("review-decks");
  const updatedUserRatings = buildUpdatedUserRatingsForCard({
    card: currentCardData,
    user,
    answer: answerFromUser || "",
    understandingRating: knowledgeUnderstandingRatingNumber,
    isDailyReview: isUserDoingDailyReview,
    ratedAt: currentDate,
  });
  const updatedRatingsHistory = updatedUserRatings.understanding_ratings_history_object_list || [];
  const storedPreviousProgressScoreForCard = cardStore.getState().previousProgressScoreForCard;
  const storedCurrentDeckProgressScore = useReviewDeckStore.getState().currentDeckProgressScore;

  let newProgressScoreForCard = getCurrentProgressScoreForDeckReview(
    currentCardData,
    user,
    knowledgeUnderstandingRatingNumber
  );
  // Retry mode swaps cards independently from the main deck index, so the cached
  // score can briefly be unavailable. Derive it from the card to keep the deck
  // total finite and avoid counting the card's existing progress twice.
  const previousProgressScoreForCard = getFiniteReviewProgressScore(
    storedPreviousProgressScoreForCard,
    getCurrentProgressScoreForDeckReview(currentCardData, user)
  );
  const currentDeckProgressScore = getFiniteReviewProgressScore(storedCurrentDeckProgressScore);
  useReviewDeckStore
    .getState()
    .setCurrentDeckProgressScore(currentDeckProgressScore + newProgressScoreForCard - previousProgressScoreForCard);
  // An answer that takes the learner across a mark on the progress bar is
  // remembered, for the next press of "next" (reviewProgressMarksStore.ts).
  noteReviewProgressChangedByAnswer({
    previousDeckProgressScore: currentDeckProgressScore,
    newDeckProgressScore: currentDeckProgressScore + newProgressScoreForCard - previousProgressScoreForCard,
    maxDeckProgressScore: useReviewDeckStore.getState().maxDeckProgressScore,
  });
  cardStore.getState().setPreviousProgressScoreForCard(newProgressScoreForCard);

  // Emphasize the newly-earned segment of the review progress bar — but only
  // when this answer was CORRECT and actually added progress. The emphasis is
  // tied to this card's id so it clears when the user moves to another question
  // (an incorrect / non-advancing answer clears any prior emphasis). This is the
  // MCQ / True-False answer path; the free-response rating path sets the same
  // emphasis in CardNavigatorComponent.saveKnowledgeUnderstandingRatingToDB.
  //
  // "Correct" depends on the question type: a correct MCQ answer arrives as
  // UNDERSTANDING_RATING_FOR_GETTING_MCQ_CORRECT (3) while a correct True/False
  // answer arrives as UNDERSTANDING_RATING_FOR_GETTING_TRUE_OR_FALSE_QUESTION_CORRECT
  // (2) — the two auto-graded types use different correct-rating values, so we
  // accept either. The deckProgressDeltaForThisAnswer > 0 guard keeps the glow
  // off when no new progress was earned (e.g. the answer-feedback replay path
  // that re-applies an already-counted rating).
  const deckProgressDeltaForThisAnswer = newProgressScoreForCard - previousProgressScoreForCard;
  const wasThisAnswerCorrect =
    knowledgeUnderstandingRatingNumber === UNDERSTANDING_RATING_FOR_GETTING_MCQ_CORRECT ||
    knowledgeUnderstandingRatingNumber === UNDERSTANDING_RATING_FOR_GETTING_TRUE_OR_FALSE_QUESTION_CORRECT;
  if (wasThisAnswerCorrect && deckProgressDeltaForThisAnswer > 0 && currentCardData?.id) {
    useReviewDeckStore.getState().setProgressDeltaEmphasis({
      cardId: currentCardData.id,
      startScore: currentDeckProgressScore,
    });
  } else {
    useReviewDeckStore.getState().setProgressDeltaEmphasis(null);
  }

  const newXPScore = user
    ? getCurrentCardRatingXPScoreForDeckReview(currentCardData, user, knowledgeUnderstandingRatingNumber)
    : 0;
  cardStore.getState().setXpScoreToAddForCurrentCard(newXPScore);

  const doesUserHaveRemainingAIExplanations =
    userSubscriptionPlan !== UserSubscriptionPlan.FREE || userNumOfChatMessagesRemaining > 0;

  // Create the updated card data, updating the nested understanding_ratings_for_card_by_user object
  const updatedCardData: CardData = {
    ...currentCardData,
    should_show_answer_feedback: doesUserHaveRemainingAIExplanations, // only show answer feedback if the user has explanations remaining
    understanding_ratings_for_card_by_user: {
      ...currentCardData?.understanding_ratings_for_card_by_user,
      [ratingOwnerId]: {
        ...updatedUserRatings,
        // Ensure dates are Date objects
        most_recent_date_rated: new Date(updatedUserRatings.most_recent_date_rated),
        understanding_ratings_history_object_list: updatedRatingsHistory.map((rating) => ({
          ...rating,
          date_rated: new Date(rating.date_rated),
        })),
      },
    },
  };

  updateCardFromCardsDataState(updatedCardData);
  // The answers of a first review fixed before signing up are kept with it, for a reload and its counted progress
  // (answerFirstReview.ts; any other card is ignored there)
  noteAnswerInAnswerFirstReview(currentCardData?.id, knowledgeUnderstandingRatingNumber, answerFromUser || "");

  if (user) {
    if (options?.persistToDatabase !== false) {
      // update the db with the new ratings object for the current user
      const cardRef = doc(db, CARDS_SAVED_COLLECTION_NAME, currentCardData.id);
      updateDoc(cardRef, {
        [`understanding_ratings_for_card_by_user.${user.id}`]: updatedUserRatings,
      });
    }
  } else if (options?.persistNonAuthenticatedRating !== false) {
    // update state for non authenticated users
    cardStore
      .getState()
      .addUnderstandingRatingForCardForNonAuthenticatedUser(currentCardData, knowledgeUnderstandingRatingNumber);
  }
  calculateNextFrfsReviewForCard(updatedCardData, knowledgeUnderstandingRatingNumber, user);

  return updatedCardData;
}

export function getCurrentProgressScoreForDeckReview(
  currentCardData: CardData,
  user: UserData,
  knowledgeUnderstandingRatingNumber?: number
) {
  /**
   * The progress bar now reflects the state of each question.
   * For MCQ and True/False questions, the values are 0 = unanswered, 1 = answered incorrectly, 2 = answered correctly.
   * For Open Questions, the values are 0 = unanswered, 1 = not sure, 2 = okay, 3 = I know it.
   * The progress percentage is calculated based on these values, with overall progress reflecting the correctness of the answers.
   */
  const isUserDoingDailyReview = window.location.pathname.includes("review-decks");
  let understandingRatingsForCardByUser = currentCardData?.understanding_ratings_for_card_by_user?.[user?.id] || {};
  // finalCardRatingScoreForXP is the final rating value that will be used to calculate XP
  // Initially set to currentKnowledgeUnderstandingRatingNumber (the new rating if provided)
  // If no new rating is provided, it falls back to previousCardRating
  // finalCardRatingScoreForXP is not xp score, but the rating for knowledge understanding rating (e.g. 1 = don't know, 2 = okay, 3= i know it)
  let finalCardRatingScoreForXP = knowledgeUnderstandingRatingNumber;
  if (!finalCardRatingScoreForXP) {
    finalCardRatingScoreForXP = -1;
    if (isUserDoingDailyReview) {
      finalCardRatingScoreForXP =
        understandingRatingsForCardByUser?.most_recent_spaced_repetition_id_of_knowledge_rating ===
        user?.current_spaced_repetition_review?.id
          ? understandingRatingsForCardByUser?.most_recent_spaced_repetition_knowledge_rating
          : -1;
    } else {
      finalCardRatingScoreForXP = understandingRatingsForCardByUser?.most_recent_knowledge_understanding_rating || -1;
    }
  }

  // when the card has not been rated yet.
  if (finalCardRatingScoreForXP < 1) {
    return 0;
  }

  const isMCQCard =
    currentCardData.card_type === CardType.RegularMultipleChoiceQuestion ||
    currentCardData.card_type === CardType.CaseScenarioMultipleChoiceQuestion ||
    currentCardData.card_type === CardType.USMLEStep1MCQ;

  const isCardTrueOrFalseQuestion = currentCardData.card_type === CardType.TrueOrFalseQuestion;

  // to get the initial rate of an MCQ
  if (isMCQCard || isCardTrueOrFalseQuestion) {
    if (finalCardRatingScoreForXP > 1) {
      return 2;
    }
    return 1;
  }

  //For a "Free response questions" and not had rated now
  return finalCardRatingScoreForXP;
}

export function getCurrentCardRatingXPScoreForDeckReview(
  cardData: CardData,
  userData: UserData,
  currentAnswerRating: number,
  shouldGetAnswerRatingFromMostRecentRating: boolean = false
) {
  // -1 means the card has never been answered before by the user.
  enum PreviousAnswerState {
    NotAnswered = -1,
  }

  /**
   * Table key format:
   * `${previousAnswer}_${currentAnswer}`
   * Example: "-1_2" → First time answering and answer is correct
   */
  type XPTableKey = `${number}_${number}`;

  /**
   * XP decision table.
   * Maps a (previous answer, current answer) pair to an XP value.
   */
  type XPDecisionTable = Record<XPTableKey, number>;
  /**
   * previous / current knowledge levels:
   *   1 = Not sure
   *   2 = OK
   *   3 = I know
   */
  const FREE_RESPONSE_XP_TABLE: XPDecisionTable = {
    "-1_1": 2, // First time, user is not sure
    "-1_2": 4, // First time, user has some confidence
    "-1_3": 6, // First time, strong confidence

    "1_1": 0, // Not sure → Not sure
    "1_2": 2, // Not sure → OK
    "1_3": 4, // Not sure → I know

    "2_1": 0, // OK → Not sure (regression)
    "2_2": 1, // OK → OK
    "2_3": 2, // OK → I know

    "3_1": 0, // I know → Not sure
    "3_2": 0, // I know → OK
    "3_3": 2, // I know → I know (reinforcement)
  };

  /**
   * True/False XP Rules
   * Same semantics as MCQ (binary correctness).
   */
  const TRUE_FALSE_XP_TABLE: XPDecisionTable = {
    "2_1": 0,
    "1_1": 0,
    "-1_1": 1,
    "2_2": 1,
    "1_2": 2,
    "-1_2": 2,
  };

  /**
   * MCQ XP Rules
   *
   * Answer:
   *  -1 = not answered before
   *   1 = incorrect
   *   3 = correct
   */
  const MCQ_XP_TABLE: XPDecisionTable = {
    "3_1": 0, // Correct → Incorrect (regression)
    "1_1": 0, // Incorrect → Incorrect (no improvement)
    "-1_1": 1, // First attempt, incorrect answer
    "3_3": 1, // Correct → Correct (reinforcement)
    "1_3": 2, // Incorrect → Correct (learning improvement)
    "-1_3": 3, // First attempt, correct answer
  };

  /**
   * If the card was not answered in this interaction,
   * no XP should be awarded.
   */
  if (!currentAnswerRating) {
    return 0;
  }

  const isDailyReview = window.location.pathname.includes("review-decks");
  const userCardRatings = cardData?.understanding_ratings_for_card_by_user?.[userData.id] ?? {};

  /**
   * Determine the previous answer rating.
   * -1 means the card has never been answered in this context.
   */
  let previousAnswerRating = -1;
  if (isDailyReview) {
    if (shouldGetAnswerRatingFromMostRecentRating) {
      previousAnswerRating =
        userCardRatings?.most_recent_spaced_repetition_id_of_knowledge_rating ===
        userData?.current_spaced_repetition_review?.id
          ? userCardRatings?.most_recent_spaced_repetition_knowledge_rating
          : PreviousAnswerState.NotAnswered;
    } else {
      // search for the second-to-last answer rating in the history object list where the spaced_repetition_id_of_knowledge_rating is the same as the current spaced repetition id
      const ratingsList = userCardRatings?.understanding_ratings_history_object_list;
      const matchingRatings = ratingsList?.filter(
        (rating) => rating.spaced_repetition_id_of_knowledge_rating === userData?.current_spaced_repetition_review?.id
      );
      if (matchingRatings && matchingRatings.length >= 2) {
        previousAnswerRating =
          matchingRatings[matchingRatings.length - 1]?.understanding_rating ?? PreviousAnswerState.NotAnswered;
      } else {
        previousAnswerRating = PreviousAnswerState.NotAnswered;
      }
    }
  } else {
    if (shouldGetAnswerRatingFromMostRecentRating) {
      previousAnswerRating =
        userCardRatings?.most_recent_knowledge_understanding_rating ?? PreviousAnswerState.NotAnswered;
    } else {
      const ratingsList = userCardRatings?.understanding_ratings_history_object_list;
      const matchingRatings = ratingsList?.filter((rating) => !rating.spaced_repetition_id_of_knowledge_rating);
      if (matchingRatings && matchingRatings.length >= 2) {
        previousAnswerRating =
          matchingRatings[matchingRatings.length - 1]?.understanding_rating ?? PreviousAnswerState.NotAnswered;
      } else {
        previousAnswerRating = PreviousAnswerState.NotAnswered;
      }
    }
  }

  /**
   * Build the lookup key for the XP decision table.
   * Example: "-1_2"
   */
  const decisionKey: XPTableKey = `${previousAnswerRating}_${currentAnswerRating}`;

  /**
   * Identify the card type to select the correct XP table.
   */
  const isMultipleChoice =
    cardData.card_type === CardType.RegularMultipleChoiceQuestion ||
    cardData.card_type === CardType.CaseScenarioMultipleChoiceQuestion ||
    cardData.card_type === CardType.USMLEStep1MCQ;

  const isTrueFalse = cardData.card_type === CardType.TrueOrFalseQuestion;

  /**
   * Select the appropriate XP table based on card type.
   */
  const xpTable: XPDecisionTable = isMultipleChoice
    ? MCQ_XP_TABLE
    : isTrueFalse
      ? TRUE_FALSE_XP_TABLE
      : FREE_RESPONSE_XP_TABLE;

  /**
   * Return the XP value from the table.
   * If no rule matches, default to 0 XP.
   */
  return xpTable[decisionKey] ?? 0;
}

export async function createDeckProgressScoresAndXpScore(deckId: string, user: UserData, currentCardData: CardData) {
  // Compute initial deck progress scores if they don't exist in the deck database
  // Use the full deck progress score for leaderboard XP to include all progress,
  // including older cards that might not have been counted initially
  const { currentDeckProgressScore, maxDeckProgressScore } = await computeInitialDeckMasteryProgressScores(
    deckId,
    user,
    currentCardData
  );

  const currentCardMostRecentRating =
    currentCardData.understanding_ratings_for_card_by_user[user?.id].most_recent_knowledge_understanding_rating;

  const updatedDeckProgressScore = currentDeckProgressScore + currentCardMostRecentRating;

  return {
    updatedDeckProgressScore,
    maxDeckProgressScore,
    xpScoreToAddForCurrentCard: currentCardMostRecentRating,
  };
}

export const createOrUpdateDailyReviewProgressAndLeaderboardXp = async (
  user: UserData | undefined,
  currentCardData: CardData,
  xpScoreEarnedForAnswer?: number
) => {
  let cardsData = cardStore.getState().cardsData;
  // Update the current card data in the cardsData state
  const index = cardsData.findIndex((card) => card.id === currentCardData.id);
  if (index !== -1) {
    cardsData[index] = currentCardData;
  }

  // Add XP to user
  if (user?.id) {
    const leaderboardRef = doc(db, LEADERBOARD_COLLECTION_NAME, user?.id);
    const leaderboardDoc = await getDoc(leaderboardRef);
    if (leaderboardDoc.exists()) {
      updateLeaderboardScoreForUser(user, xpScoreEarnedForAnswer);
    }
  }
};

export const createOrUpdateDeckProgressAndLeaderboardCollection = async (
  deckId: string,
  user: UserData | undefined
) => {
  if (!deckId) {
    return;
  }
  let currentDeckProgressScore = useReviewDeckStore.getState().currentDeckProgressScore || 0;
  const deckRef = doc(db, DECK_COLLECTION_NAME, deckId);
  const deckSnap = await getDoc(deckRef);
  const deckData = deckSnap.data();
  let { deck_progress_scores_and_animation_state_by_user: deckProgressScoresAndAnimationStateByUser = {} } = deckData;

  // newXpScoreToAddForCurrentCard can be positive or negative depending on whether the user improved or worsened from their previous answer
  let newMaxDeckProgressScore = useReviewDeckStore.getState().maxDeckProgressScore || 0;
  deckProgressScoresAndAnimationStateByUser[user?.id] = {
    current_deck_progress_score: currentDeckProgressScore || 0,
    max_deck_progress_score: newMaxDeckProgressScore || 1,
    has_fixed_deck_progress_score: true,
    lastest_answer_date: new Date().toISOString(), //use ISO rathern UTC because it is easier to convert ot back to Date format
  };

  if (user?.id) {
    const leaderboardRef = doc(db, LEADERBOARD_COLLECTION_NAME, user?.id);
    const leaderboardDoc = await getDoc(leaderboardRef);
    if (!leaderboardDoc.exists()) {
      updateLeaderboardForUser(user.id, {
        user_id: user.id,
      });
    }
  }

  try {
    await updateDoc(deckRef, {
      deck_progress_scores_and_animation_state_by_user: deckProgressScoresAndAnimationStateByUser,
    });
  } catch (error) {
    console.error("Error updating deck progress score:", error);
  }
};

function orderCardsWithCaseScenarios(cardsData: CardData[]): CardData[] {
  let cardsDataWithOrderedCaseScenarios: CardData[] = [];
  let processedCaseScenarios = new Set<string>();

  for (const card of cardsData) {
    if (card.case_scenario_details && !processedCaseScenarios.has(card.case_scenario_details)) {
      // This is a new case scenario
      const caseScenarioCards = cardsData.filter((c) => c.case_scenario_details === card.case_scenario_details);
      cardsDataWithOrderedCaseScenarios.push(...caseScenarioCards);
      processedCaseScenarios.add(card.case_scenario_details);
    } else if (!card.case_scenario_details) {
      // This card doesn't have a case scenario, add it individually
      cardsDataWithOrderedCaseScenarios.push(card);
    }
    // If the card has a case scenario that's already been processed, skip it
  }

  return cardsDataWithOrderedCaseScenarios;
}

export const orderCardsToHaveTheFirstFourBeMultipleChoiceQuestionsAndGroupCaseQuestions = (
  cardsData: CardData[],
  indexOfCurrentCard: number
) => {
  // Return early if cardsData is null or undefined
  if (!cardsData) {
    return cardsData;
  }

  // this function maintains the same order of cardsData but groups cards with the same case scenario together
  let orderedCards = orderCardsWithCaseScenarios(cardsData);

  // ***** START MAKING FIRST FOUR CARDS TO BE MULTIPLE CHOICE QUESTIONS *****
  // Find the first 4 multiple choice questions in the ordered cards
  const firstFourMCQs: CardData[] = [];
  for (const card of orderedCards) {
    if (card.card_type === CardType.RegularMultipleChoiceQuestion || card.card_type === CardType.USMLEStep1MCQ) {
      firstFourMCQs.push(card);
    }
    if (firstFourMCQs.length >= 4) {
      break;
    }
  }

  // if there are 0 standard multiple choice questions, add case scenario multiple choice questions to the list until there are 4 multiple choice questions
  if (firstFourMCQs.length === 0) {
    for (const card of orderedCards) {
      if (card.card_type === CardType.CaseScenarioMultipleChoiceQuestion) {
        firstFourMCQs.push(card);
      }
      if (firstFourMCQs.length >= 4) {
        break;
      }
    }
  }

  // If there are no multiple choice questions, return the ordered cards as is
  if (firstFourMCQs.length === 0) {
    return orderedCards;
  }

  // Check if the first 4 cards are already multiple choice questions
  const numOfMCQsInFirstFour = orderedCards
    .slice(0, 4)
    .filter(
      (card) => card.card_type === CardType.RegularMultipleChoiceQuestion || card.card_type === CardType.USMLEStep1MCQ
    ).length;
  if (numOfMCQsInFirstFour >= 4) {
    return orderedCards;
  }

  // Rearrange the cards to bring the first 4 MCQs to the top, preserving the relative order
  const mcqsToMove = new Set(firstFourMCQs);
  const rearrangedCards: CardData[] = [];
  const remainingCards: CardData[] = [];
  let mcqCount = 0;

  for (const card of orderedCards) {
    if (mcqsToMove.has(card) && mcqCount < 4) {
      rearrangedCards.push(card);
      mcqCount++;
      mcqsToMove.delete(card);
    } else {
      remainingCards.push(card);
    }
  }

  // Combine the first 4 MCQs with the remaining cards
  const finalOrderedCards = [...rearrangedCards, ...remainingCards];

  return finalOrderedCards;
};

export const calculateConceptAndTopicsMasteryProgressScores = (
  cardIds: string[],
  user: UserData,
  cardsData: CardData[]
) => {
  let updatedCardsData = cardsData;

  const conceptMasteryProgressScoresDataGroupedByConcepts = {};

  const cardIdsSet = new Set(cardIds);
  let hasUserAnsweredAQuestion = false;
  // lets get the max possible mastery progress scores for concepts and topics first
  updatedCardsData?.forEach((card) => {
    if (
      card &&
      ((card?.understanding_ratings_for_card_by_user && card?.understanding_ratings_for_card_by_user[user?.id]) ||
        (card?.understanding_ratings_for_topics_by_user && card?.understanding_ratings_for_topics_by_user[user?.id]))
    ) {
      hasUserAnsweredAQuestion = true;
    }

    if (!card || !card.card_key_concept_name) return;

    const keyConceptName = card.card_key_concept_name;
    const currentCardTopics = card.card_topics;
    let mostRecentKnowledgeUnderstandingRatingForACard = 0;
    if (card.understanding_ratings_for_card_by_user && card.understanding_ratings_for_card_by_user[user?.id]) {
      mostRecentKnowledgeUnderstandingRatingForACard =
        card.understanding_ratings_for_card_by_user[user?.id].most_recent_knowledge_understanding_rating;
    }
    // TEMPORARILY adding this because Julian did migration before we changed name of fields
    if (card.understanding_ratings_for_topics_by_user && card.understanding_ratings_for_topics_by_user[user?.id]) {
      mostRecentKnowledgeUnderstandingRatingForACard =
        card.understanding_ratings_for_topics_by_user[user?.id].knowledge_understanding_rating;
    }

    // initialize object for concept if it hasn't been initialized for the concept
    if (!conceptMasteryProgressScoresDataGroupedByConcepts[keyConceptName]) {
      conceptMasteryProgressScoresDataGroupedByConcepts[keyConceptName] = {
        topicNamesAndMasteryProgressScores: {},
        maxConceptScore: 0,
        currConceptScore: 0,
      };
    }
    const maxPossibleScoreForCard = (() => {
      if (
        card.card_type === CardType.RegularMultipleChoiceQuestion ||
        card.card_type === CardType.CaseScenarioMultipleChoiceQuestion ||
        card.card_type === CardType.USMLEStep1MCQ
      ) {
        return UNDERSTANDING_RATING_FOR_GETTING_MCQ_CORRECT;
      } else if (card.card_type === CardType.TrueOrFalseQuestion) {
        return UNDERSTANDING_RATING_FOR_GETTING_TRUE_OR_FALSE_QUESTION_CORRECT;
      } else {
        return UNDERSTANDING_RATING_FOR_GETTING_FREE_RESPONSE_AND_CASE_SCENARIO_CORRECT;
      }
    })();

    // aggregate max possible score for the concept
    conceptMasteryProgressScoresDataGroupedByConcepts[keyConceptName].maxConceptScore += maxPossibleScoreForCard;

    if (cardIdsSet.has(card?.id)) {
      // add the understanding rating of the card to the current score for the concept only if they have seen the card already
      conceptMasteryProgressScoresDataGroupedByConcepts[keyConceptName].currConceptScore +=
        mostRecentKnowledgeUnderstandingRatingForACard;
    }

    // old cards and manually created cards may not have topics
    if (!currentCardTopics) return;

    // go through each topic of the card and add the max possible score topic the topic
    for (const topicName of currentCardTopics) {
      if (
        !conceptMasteryProgressScoresDataGroupedByConcepts[keyConceptName].topicNamesAndMasteryProgressScores[topicName]
      ) {
        conceptMasteryProgressScoresDataGroupedByConcepts[keyConceptName].topicNamesAndMasteryProgressScores[
          topicName
        ] = {
          currentScore: NaN, //initialize as NaN to mark a state that users haven't seen the topic yet
          maxPossibleScore: 0,
        };
      }

      conceptMasteryProgressScoresDataGroupedByConcepts[keyConceptName].topicNamesAndMasteryProgressScores[
        topicName
      ].maxPossibleScore += maxPossibleScoreForCard;

      if (cardIdsSet.has(card?.id)) {
        // Initialize currentScore to 0 if it's NaN before adding the understanding rating
        const topicMasteryProgressScores =
          conceptMasteryProgressScoresDataGroupedByConcepts[keyConceptName].topicNamesAndMasteryProgressScores[
            topicName
          ];
        topicMasteryProgressScores.currentScore = isNaN(topicMasteryProgressScores.currentScore)
          ? 0
          : topicMasteryProgressScores.currentScore;

        // Add the understanding rating of the card to the current score for the topic only if they have seen the card already
        topicMasteryProgressScores.currentScore += mostRecentKnowledgeUnderstandingRatingForACard;
      }
    }
  });

  return {
    conceptMasteryProgressScoresDataGroupedByConcepts,
    hasUserAnsweredAQuestion,
  };
};

export const computeInitialDeckMasteryProgressScores = async (
  deckId: string,
  user: UserData,
  currentCardData: CardData
): Promise<{ currentDeckProgressScore: number; maxDeckProgressScore: number }> => {
  // Computes the initial deck mastery progress scores by going through all cards when the scores haven't existed in the database yet
  // Fetch deck and cards data
  const deck = await getDeckById(deckId, user);
  const cardsData = await getCardsByDeckId(deckId, user);

  if (!deck || !cardsData) {
    throw new Error("Failed to fetch deck or cards data");
  }

  let currentDeckProgressScore = 0;
  let maxDeckProgressScore = 0;

  cardsData.forEach((card) => {
    if (!card) return;

    const maxExpectedScoreForCard = (() => {
      if (
        card.card_type === CardType.RegularMultipleChoiceQuestion ||
        card.card_type === CardType.CaseScenarioMultipleChoiceQuestion ||
        card.card_type === CardType.USMLEStep1MCQ
      ) {
        return UNDERSTANDING_RATING_FOR_GETTING_MCQ_CORRECT;
      } else if (card.card_type === CardType.TrueOrFalseQuestion) {
        return UNDERSTANDING_RATING_FOR_GETTING_TRUE_OR_FALSE_QUESTION_CORRECT;
      } else {
        return UNDERSTANDING_RATING_FOR_GETTING_FREE_RESPONSE_AND_CASE_SCENARIO_CORRECT;
      }
    })();

    let mostRecentKnowledgeUnderstandingRatingForACard = 0;
    if (card.understanding_ratings_for_card_by_user && card.understanding_ratings_for_card_by_user[user?.id]) {
      mostRecentKnowledgeUnderstandingRatingForACard =
        card.understanding_ratings_for_card_by_user[user?.id].most_recent_knowledge_understanding_rating;
    }

    const effectiveMaxScoreForCard = Math.max(maxExpectedScoreForCard, mostRecentKnowledgeUnderstandingRatingForACard);
    maxDeckProgressScore += effectiveMaxScoreForCard;

    // Add the understanding rating of the card to the current score
    if (card.id !== currentCardData.id) {
      currentDeckProgressScore += mostRecentKnowledgeUnderstandingRatingForACard;
    }
  });

  return { currentDeckProgressScore, maxDeckProgressScore };
};

export function computeReviewDeckMasteryProgressScores(cardsData, user: UserData) {
  // const isUserDoingDailyReview = window.location.pathname.includes("review-decks");
  let maxPossibleDeckProgressScore = 0;
  let currentDeckProgressScore = 0;

  cardsData?.forEach((card) => {
    // Determine if the card is a Multiple Choice Question (MCQ).
    const isCardMCQ =
      card.card_type === CardType.RegularMultipleChoiceQuestion ||
      card.card_type === CardType.CaseScenarioMultipleChoiceQuestion ||
      card.card_type === CardType.USMLEStep1MCQ;

    const isCardTrueOrFalseQuestion = card.card_type === CardType.TrueOrFalseQuestion;

    if (isCardMCQ || isCardTrueOrFalseQuestion) {
      maxPossibleDeckProgressScore += 2;
    } else {
      maxPossibleDeckProgressScore += 3;
    }
    const currentScore = getCurrentProgressScoreForDeckReview(card, user);
    currentDeckProgressScore += currentScore;
  });
  return { maxPossibleDeckProgressScore, currentDeckProgressScore };
}

export const truncateText = (text: string | undefined, limit: number): string => {
  if (text && text.length > limit) {
    return text.substring(0, limit) + "...";
  }
  return text || "";
};

export const calculateNumOfCardsPerCheckpoint = (
  numOfSetsGenerated: number,
  indexOfCurrentCard: number,
  numOfQuestions: number
): number => {
  let numOfCardsPerCheckpoint = numOfSetsGenerated <= 1 && indexOfCurrentCard < 10 ? indexOfCurrentCard + 1 : 10;

  if (numOfCardsPerCheckpoint === 10 && numOfQuestions >= 8 && numOfQuestions < 20 && numOfSetsGenerated > 1) {
    numOfCardsPerCheckpoint = Math.floor(numOfQuestions / 2);
  }

  return numOfCardsPerCheckpoint;
};

export const filterCardsWithMistakes = (
  cards: CardData[],
  user: any,
  options?: { includeHistoricalMistakes?: boolean }
): CardData[] => {
  const isUserDoingDailyReview = window.location.pathname.includes("review-decks");
  return cards?.filter((card) =>
    cardHasPastMistake(card, user?.id, {
      isDailyReview: isUserDoingDailyReview,
      currentReviewId: user?.current_spaced_repetition_review?.id,
      includeHistoricalMistakes: options?.includeHistoricalMistakes,
    })
  );
};

export const resetAnswersForMultipleChoiceQuestionsInCardsData = (cardsData: CardData[]): CardData[] => {
  return cardsData.map((card) => ({
    ...card,
    selectedAnswerText:
      card?.card_type === CardType.RegularMultipleChoiceQuestion || card?.card_type === CardType.USMLEStep1MCQ
        ? null
        : undefined,
  }));
};

export async function getSpacedRepetitionReviewCardsForDeck(user, deckId) {
  try {
    const response = await fetch(
      `${JUNGLE_CLOUDFLARE_BASE_URL}/spaced_repetition/get_spaced_repetition_review_cards_for_deck_id/${deckId}`,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          user_id: user?.id,
        }),
      }
    );
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    return (await response.json()).cards_for_review;
  } catch (error) {
    trackError(error, {
      scope: "review-priority-order",
      fingerprint: ["review-priority-order", "spaced-repetition-review-load-failed"],
      userId: user?.id,
      level: "warning",
      tags: { error_kind: "spaced-repetition-review-load-failed" },
      extra: { deckId },
    });
    return null;
  }
}

export const areQuestionsCurrentlyBeingGeneratedMethod = (deckData: DeckObject): boolean => {
  let deckCreatedDate = null;
  let currentDate = new Date();
  if (typeof deckData?.date_created === "string") {
    deckCreatedDate = new Date(deckData?.date_created as string);
  } else {
    deckCreatedDate = new Date(deckData?.date_created?.seconds * 1000);
  }
  const hoursSinceDeckCreation = (currentDate?.getTime() - deckCreatedDate?.getTime()) / (1000 * 60 * 60);
  // if deck is created more than 1 hour ago, then we can assume that questions are not being generated
  // on localhost time is in UTC so this gets messed up, so we'll ignore this condition if we're on localhost
  if (hoursSinceDeckCreation > 1 && !window.location.hostname.includes("localhost")) {
    return false;
  }
  if (deckData?.generation_metadata?.has_been_generated_from_multiple_documents) {
    return false;
  }
  return (
    deckData?.is_deck_generated_from_document && // only show loading state if deck is generated from document
    (deckData?.generation_metadata?.is_processing_occlusion_images ||
      deckData?.generation_metadata?.is_generating_image_occlusion_cards_for_image ||
      (deckData?.generation_metadata?.active_number_of_question_generation_requests != null &&
        (deckData?.generation_metadata?.active_number_of_question_generation_requests > 0 ||
          deckData?.generation_metadata?.is_page_generation_in_progress)) ||
      deckData?.generation_metadata?.active_number_of_question_generation_requests == null) // if active_number_of_question_generation_requests is undefined, then we can assume that questions are not being generated
    // deckData?.generation_metadata?.is_currently_generating_knowledge_tree
  );
};

// Identify if the deck is a class
export const isDeckIsAClass = (deckData: DeckObject): boolean => {
  const isAClass = !!(
    deckData?.userThatCreatedDeck?.user_type === UserType.Student ||
    deckData?.userThatCreatedDeck?.user_type === UserType.TeacherOrProfessor ||
    deckData?.is_deck_a_class
  );
  return isAClass || false;
};

export const getCurrentWeekKeyNameIdentifier = (): string => {
  const currentDate = new Date();
  const utcCurrentDate = new Date(
    Date.UTC(
      currentDate.getUTCFullYear(),
      currentDate.getUTCMonth(),
      currentDate.getUTCDate(),
      currentDate.getUTCHours(),
      currentDate.getUTCMinutes(),
      currentDate.getUTCSeconds()
    )
  );

  return `${utcCurrentDate.getUTCFullYear()}-W${String(getISOWeek(utcCurrentDate)).padStart(2, "0")}`; // pad 0. e.g. "2024-W5" -> "2024-W05"
};

export const getCurrentMonthKeyNameIdentifier = (): string => {
  const currentDate = new Date();
  const utcCurrentDate = new Date(
    Date.UTC(
      currentDate.getUTCFullYear(),
      currentDate.getUTCMonth(),
      currentDate.getUTCDate(),
      currentDate.getUTCHours(),
      currentDate.getUTCMinutes(),
      currentDate.getUTCSeconds()
    )
  );
  return `${utcCurrentDate.getUTCFullYear()}-${String(utcCurrentDate.getUTCMonth() + 1).padStart(2, "0")}`; // pad 0. e.g. "2024-01"
};

export const getPresignedUrlForImageKey = async (objectKey: string): Promise<string> => {
  try {
    return await getSignedExtractedImageUrl(objectKey);
  } catch (error) {
    trackEvent("Error: Failed to getPresignedUrlForImageKey", {
      errorMessage: error instanceof Error ? error.message : String(error),
    });
  }
};

export function getAnimalCharacterWithAccessoryImage(
  characterWithAccessorySelected: AnimalCharacterWithAccessory,
  shouldGetRandomAnimalIfNull = false,
  userId?: string
) {
  switch (characterWithAccessorySelected) {
    case AnimalCharacterWithAccessory.CapybaraNoAccessory:
      return CapybaraWithNoAccessorySvg;
    case AnimalCharacterWithAccessory.CapybaraWithGlasses:
      return CapybaraWithGlassesSvg;
    case AnimalCharacterWithAccessory.CapybaraWithFruit:
      return CapybaraWithFruitSvg;
    case AnimalCharacterWithAccessory.MonkeyNoAccessory:
      return MonkeyWithNoAccessorySvg;
    case AnimalCharacterWithAccessory.MonkeyWithGlasses:
      return MonkeyWithGlassesSvg;
    case AnimalCharacterWithAccessory.MonkeyWithBanana:
      return MonkeyWithBananaSvg;
    case AnimalCharacterWithAccessory.SlothNoAccessory:
      return SlothWithNoAccessorySvg;
    case AnimalCharacterWithAccessory.SlothWithGlasses:
      return SlothWithGlassesSvg;
    case AnimalCharacterWithAccessory.SlothWithFruit:
      return SlothWithFruitSvg;
    default:
      if (shouldGetRandomAnimalIfNull && userId) {
        const allCharacters = [
          CapybaraWithNoAccessorySvg,
          CapybaraWithGlassesSvg,
          SlothWithNoAccessorySvg,
          SlothWithGlassesSvg,
          SlothWithFruitSvg,
          MonkeyWithNoAccessorySvg,
          MonkeyWithGlassesSvg,
          MonkeyWithBananaSvg,
        ];
        // Use userId to get a random but specific avatar for a user that hasn't selected an avatar yet
        // this is used for the leaderboard component
        const hash = userId.split("").reduce((acc, char) => {
          return char.charCodeAt(0) + ((acc << 5) - acc);
        }, 0);
        const index = Math.abs(hash) % allCharacters.length;
        return allCharacters[index];
      }
      return null;
  }
}

export function shouldShowAnswerFeedbacksOrChatMessagesRemainingMessage(
  user: UserData,
  userSubscriptionPlan: UserSubscriptionPlan
): boolean {
  // if user hasn't been created yet, don't show answer feedbacks or chat messages remaining message
  if (!user) {
    return false;
  }

  // if user is in mini review mode, we will let them use chat and feedbacks feature for free
  const isUserInMiniReviewMode = cardStore.getState().isUserInMiniReviewMode;
  if (isUserInMiniReviewMode) {
    return false;
  }

  // All paid subscription plans have unlimited chat messages.
  if (userSubscriptionPlan === UserSubscriptionPlan.FREE) {
    return true;
  }
  return false;
}

// Add this helper function to check if a date is a FirebaseDate
export function isFirebaseDate(date: Date | FirebaseDate | string | any): date is FirebaseDate {
  if (!date) {
    return false;
  }
  // Check if it's a string or number (not a Firebase Timestamp)
  if (typeof date === "string" || typeof date === "number") {
    return false;
  }
  // Check if it's a Date instance
  if (date instanceof Date) {
    return false;
  }
  // Check if it's an object with seconds and nanoseconds properties
  return typeof date === "object" && "seconds" in date && "nanoseconds" in date;
}

export function getDateFromDatabaseField(date: Date | FirebaseDate | string | any): Date {
  if (!date) {
    return new Date(0);
  }
  // Handle string dates (ISO format)
  if (typeof date === "string") {
    return new Date(date);
  }
  // Handle Firebase Timestamp
  if (isFirebaseDate(date)) {
    return new Date(date.seconds * 1000 + date.nanoseconds / 1000000);
  }
  // Handle regular Date object
  if (date instanceof Date) {
    return date;
  }
  // Fallback - try to create a Date from the value
  return new Date(date);
}

/**
 * Max time we trust `is_review_currently_being_created` before treating it as a stuck backend state.
 * If the worker errors without clearing the flag, the client can recover and call create again.
 */
export const STALE_SPACED_REPETITION_REVIEW_CREATION_IN_PROGRESS_MS = 90 * 60 * 1000;

/**
 * Returns true when `is_review_currently_being_created` should be ignored: explicit failure from the backend,
 * or the review metadata is older than {@link STALE_SPACED_REPETITION_REVIEW_CREATION_IN_PROGRESS_MS}.
 * Prefer fixing the Cloudflare/backend path so the flag is always cleared in a `finally` block; this is a client-side safety net.
 */
export function shouldTreatSpacedRepetitionReviewCreationInProgressAsStale(
  currentSpacedRepetitionReview: UserData["current_spaced_repetition_review"] | undefined
): boolean {
  if (!currentSpacedRepetitionReview?.is_review_currently_being_created) {
    return false;
  }
  if (currentSpacedRepetitionReview.did_review_creation_fail) {
    return true;
  }
  const dateCreatedField = currentSpacedRepetitionReview.date_created;
  if (dateCreatedField == null) {
    return false;
  }
  const reviewCreatedAt = getDateFromDatabaseField(dateCreatedField);
  return Date.now() - reviewCreatedAt.getTime() > STALE_SPACED_REPETITION_REVIEW_CREATION_IN_PROGRESS_MS;
}

// Helper function to get array of dates between start and end of streak
export const getAllDaysFromStreakStartDateToEndDate = (start_date: Timestamp, end_date: Timestamp) => {
  const datesOfAllDaysInStreak = [];
  // Convert timestamps to Date objects and strip time component
  const streakStartDate = new Date(start_date.seconds * 1000);
  streakStartDate.setHours(0, 0, 0, 0);
  const streakEndDate = new Date(end_date.seconds * 1000);
  streakEndDate.setHours(0, 0, 0, 0);

  while (streakStartDate <= streakEndDate) {
    datesOfAllDaysInStreak.push(new Date(streakStartDate));
    streakStartDate.setDate(streakStartDate.getDate() + 1);
  }
  return datesOfAllDaysInStreak;
};

// Helper function to update user notification settings and track the event
const updateUserNotificationSettings = (
  user: UserData,
  locationEventWasTriggeredFrom: string,
  isDesktopNotificationEnabled: boolean,
  token: string | null = null
) => {
  const updatedUserSettings = {
    ...user?.user_settings,
    desktop_push_notification_token: token, // Update with the new token or null
    notification_preferences: {
      ...user?.user_settings?.notification_preferences,
      review_reminder_on_desktop: isDesktopNotificationEnabled,
    },
  };

  updateUser(user?.id, {
    user_settings: updatedUserSettings,
  });

  trackEvent(
    "Communication settings changed",
    {
      desktopReviewRemindersSettingChanged: isDesktopNotificationEnabled ? "on" : "off",
      tokenUpdated: !!token,
      locationEventWasTriggeredFrom: locationEventWasTriggeredFrom,
    },
    user
  );
};

/**
 * Requests permission from users to enable browser-based desktop notifications via FCM.
 * Ensures a fresh token is retrieved and stored if permission is granted.
 */
export const requestPermissionForDesktopPushNotifications = async (
  user: UserData,
  locationEventWasTriggeredFrom: string
): Promise<string | null> => {
  // Web notifications are not supported in React Native WebView
  if (window.ReactNativeWebView) {
    return null; // Return null instead of rejecting for consistency
  }

  try {
    // Ensure we are in an environment that supports service workers
    if (!("serviceWorker" in navigator)) {
      return null;
    }

    let currentPermission = Notification.permission;

    // 1) Handle "default" permission state (user hasn't decided)
    if (currentPermission === "default") {
      currentPermission = await Notification.requestPermission();

      if (currentPermission !== "granted") {
        updateUserNotificationSettings(user, locationEventWasTriggeredFrom, false, null);
        return null;
      }
    }

    // 2) Handle "denied" permission state
    if (currentPermission === "denied") {
      updateUserNotificationSettings(user, locationEventWasTriggeredFrom, false, null);
      navigationWithStatesStore.getState().setShowNotificationPermissionInstructionsModal(true);
      return null;
    }

    // 3) If permission is "granted" (either initially or after request)
    if (currentPermission === "granted") {
      let registration: ServiceWorkerRegistration | null = null;
      try {
        registration = await navigator.serviceWorker.register("/firebase-messaging-sw.js");
      } catch (swError) {
        // Optionally update settings to false here if registration is critical
        // updateUserNotificationSettings(user, locationEventWasTriggeredFrom, false, null);
        throw swError; // Re-throw to be caught by the outer catch block
      }

      // Set up listener for permission changes (optional but good practice)
      try {
        navigator.permissions?.query({ name: "notifications" })?.then((permissionStatus) => {
          permissionStatus.onchange = () => {
            if (permissionStatus.state === "denied") {
              updateUserNotificationSettings(user, locationEventWasTriggeredFrom + "_permission_change", false, null);
            } else if (permissionStatus.state === "granted") {
              // Re-request token when permissions are granted again
              if (registration && messaging) {
                getToken(messaging, { vapidKey: FIREBASE_VAP_ID_KEY, serviceWorkerRegistration: registration })
                  .then((newToken) => {
                    if (newToken) {
                      updateUserNotificationSettings(
                        user,
                        locationEventWasTriggeredFrom + "_permission_change",
                        true,
                        newToken
                      );
                    } else {
                      updateUserNotificationSettings(
                        user,
                        locationEventWasTriggeredFrom + "_permission_change",
                        false,
                        null
                      );
                    }
                  })
                  .catch((e) => {
                    updateUserNotificationSettings(
                      user,
                      locationEventWasTriggeredFrom + "_permission_change",
                      false,
                      null
                    );
                  });
              }
            }
          };
        });
      } catch (permError) {
        // console.warn("Could not set up permission change listener:", permError); // Keep this line commented out if it's intentional
      }

      // Attempt to get the FCM token
      try {
        // Check if messaging is available before attempting to get token
        if (!messaging) {
          updateUserNotificationSettings(user, locationEventWasTriggeredFrom, false, null);
          return null;
        }
        const currentToken = await getToken(messaging, {
          vapidKey: FIREBASE_VAP_ID_KEY,
          serviceWorkerRegistration: registration,
        });

        if (currentToken) {
          updateUserNotificationSettings(user, locationEventWasTriggeredFrom, true, currentToken);
          return currentToken;
        } else {
          updateUserNotificationSettings(user, locationEventWasTriggeredFrom, false, null);
          return null;
        }
      } catch (tokenError) {
        updateUserNotificationSettings(user, locationEventWasTriggeredFrom, false, null);
        return null; // Return null on token error
      }
    }

    // Fallback case (should not be reached if logic is correct)
    return null;
  } catch (error) {
    // Attempt to update settings to disabled as a safety measure
    try {
      updateUserNotificationSettings(user, locationEventWasTriggeredFrom + "_error", false, null);
    } catch (updateError) {
      // intentionally swallowing error here
    }
    return null; // Return null on general error
  }
};

/**
 * Stores the desktop push token of a learner who has already allowed notifications in this browser, without
 * asking anything: no browser prompt and no instructions modal. Asking is the opt-in screen's job, at the
 * moments notification-permission-asks.ts picks. Returns the token, or null when there was none to store.
 */
export const refreshDesktopPushNotificationTokenWithoutAsking = async (
  user: UserData,
  locationEventWasTriggeredFrom: string
): Promise<string | null> => {
  // A browser, not the mobile app's WebView (the app stores its own push token), that can run the push service
  // worker and where the learner has already allowed notifications.
  const canStoreDesktopPushTokenWithoutAsking =
    !window.ReactNativeWebView &&
    "serviceWorker" in navigator &&
    window.Notification?.permission === "granted" &&
    Boolean(messaging);
  if (!canStoreDesktopPushTokenWithoutAsking) return null;
  try {
    const serviceWorkerRegistration = await navigator.serviceWorker.register("/firebase-messaging-sw.js");
    const currentToken = await getToken(messaging, { vapidKey: FIREBASE_VAP_ID_KEY, serviceWorkerRegistration });
    if (!currentToken) return null;
    updateUserNotificationSettings(user, locationEventWasTriggeredFrom, true, currentToken);
    return currentToken;
  } catch {
    // Tried again on the next visit; the learner's setting stays as they chose it.
    return null;
  }
};

export const hasUserHasSeenGratitudeEntryScreenToday = async (userId: string) => {
  if (!userId) return false;
  try {
    const startOfToday = new Date();
    startOfToday.setHours(0, 0, 0, 0);

    const endOfToday = new Date();
    endOfToday.setHours(23, 59, 59, 999);

    // Query for user's gratitude entries from today
    const gratitudeCollectionRef = collection(db, GRATITUDE_ENTRIES_BY_USER_COLLECTION_NAME);
    const gratitudeQuery = query(
      gratitudeCollectionRef,
      where("user_id", "==", userId),
      where("most_recent_date_gratitude_wall_was_seen", ">=", startOfToday),
      where("most_recent_date_gratitude_wall_was_seen", "<=", endOfToday)
    );

    const gratitudeQuerySnapshot = await getDocs(gratitudeQuery);

    // If there are any entries today or if this is the user's first time (no gratitude ref yet),
    // return true
    return !gratitudeQuerySnapshot.empty;
  } catch (error) {
    console.error("Error checking gratitude entry:", error);
    return false;
  }
};

export async function getLatestThreeExamsSortedByDateForUser(
  userId: string,
  knownUser?: Pick<UserData, "id" | "exams_properties">
): Promise<ExamObject[]> {
  try {
    // Home already has the subscribed profile. Other callers retain the fresh
    // document lookup, and a profile from another account cannot skip it.
    const userData = knownUser?.id === userId
      ? knownUser
      : (await getDoc(doc(db, USERS_COLLECTION_NAME, userId))).data() as UserData;

    // If user has no exams, return empty array
    if (!userData?.exams_properties?.exam_ids?.length) {
      return [];
    }

    // Create a date at start of day in UTC time
    const currentDate = new Date();
    const currentDate12am = new Date(currentDate);
    currentDate12am.setHours(0, 0, 0, 0);
    const currentTimestamp = Timestamp.fromDate(currentDate12am);

    // Canonical deletes retain a tombstone. Walk past those in small bounded
    // pages, so an undone exam cannot hide the learner's next real exam.
    const sortedFutureExams: Array<{ examData: ExamObject; examId: string }> = [];
    let after: QueryDocumentSnapshot | undefined;
    for (let pageNumber = 0; pageNumber < 10 && sortedFutureExams.length < 3; pageNumber++) {
      const page = await getDocs(query(
        collection(db, EXAMS_COLLECTION_NAME),
        where("user_id", "==", userId),
        where("date_of_exam", ">=", currentTimestamp),
        orderBy("date_of_exam", "asc"),
        ...(after ? [startAfter(after)] : []),
        limit(3)
      ));
      for (const examDoc of page.docs) {
        const examData = examDoc.data() as ExamObject & { is_deleted?: boolean };
        if (examData.is_deleted === true || examData.user_id !== userId) continue;
        if (examData.date_of_exam && "seconds" in examData.date_of_exam &&
          examData.date_of_exam.seconds >= currentTimestamp.seconds) {
          sortedFutureExams.push({ examData, examId: examDoc.id });
          if (sortedFutureExams.length === 3) break;
        }
      }
      if (page.docs.length < 3 || page.docs.at(-1)?.id === after?.id) break;
      after = page.docs.at(-1);
    }
    // If no future exams, return empty immediately
    if (sortedFutureExams.length === 0) {
      return [];
    }

    const deckIds = sortedFutureExams
      .map((exam) => exam.examData.class_deck_id_that_this_exam_belongs_to)
      .filter(Boolean);

    if (deckIds.length === 0) {
      return [];
    }

    const decksQuery = query(collection(db, DECK_COLLECTION_NAME), where(documentId(), "in", [...new Set(deckIds)]));

    const deckDocs = await getDocs(decksQuery);

    // Create a map for O(1) deck lookup
    const deckMap = new Map<string, any>();
    deckDocs.docs.forEach((deckDoc) => {
      deckMap.set(deckDoc.id, deckDoc.data());
    });

    // build final result without additional async operations
    const futureExamsWithDeckData: ExamObject[] = sortedFutureExams
      .map(({ examData, examId }) => {
        const deckId = examData.class_deck_id_that_this_exam_belongs_to;
        const deckData = deckId ? deckMap.get(deckId) : null;
        // A calendar date is useful before questions or coverage are attached.
        // Keep its class name; a child document's card count is not visibility.
        if (!deckData || deckData.is_deleted === true || deckData.deleted === true || deckData.is_deleting === true) return null;
        return {
          ...examData,
          id: examId,
          class_name: deckData?.name || null,
        };
      })
      .filter((exam) => exam?.class_name); // Only include exams with valid deck data

    return futureExamsWithDeckData;
  } catch (error) {
    console.error("Error fetching exams:", error);
    return [];
  }
}

const pendingStreakReads = new Map<string, Promise<number>>();

export async function getCurrentStreakLength(userId: string): Promise<number> {
  if (!userId) return 0;
  const pendingRead = pendingStreakReads.get(userId);
  if (pendingRead) return pendingRead;

  // Nav, page wrapper, and home mount together. Share their concurrent read,
  // including a zero streak, but never cache a settled value across visits.
  const request = (async () => {
    const streakDoc = await getDoc(doc(db, STREAKS_COLLECTION_NAME, userId));
    if (!streakDoc.exists()) return 0;
    return streakDoc.data()?.number_of_days_for_current_streak;
  })();
  pendingStreakReads.set(userId, request);
  try {
    return await request;
  } finally {
    if (pendingStreakReads.get(userId) === request) pendingStreakReads.delete(userId);
  }
}
export const getQuestionDifficultyGroupFromTaxonomy = (questionTaxonomyLevel: QuestionTaxonomy) => {
  if (questionTaxonomyLevel === QuestionTaxonomy.Remember || questionTaxonomyLevel === QuestionTaxonomy.Understand) {
    return QuestionDifficultyGroup.Basic;
  } else if (
    questionTaxonomyLevel === QuestionTaxonomy.Apply ||
    questionTaxonomyLevel === QuestionTaxonomy.Analyze ||
    questionTaxonomyLevel === QuestionTaxonomy.Evaluate
  ) {
    return QuestionDifficultyGroup.Advanced;
  }
  return QuestionDifficultyGroup.Basic;
};

type CopyToClipboardOptions = {
  htmlContent?: string;
  markdownText?: string;
  plainTextFallback?: string;
  onSuccess?: () => void;
  onError?: (error: any) => void;
};

export const copyToClipboard = async (options: CopyToClipboardOptions): Promise<boolean> => {
  const { htmlContent, markdownText, plainTextFallback, onSuccess, onError } = options;

  let htmlToUse = htmlContent;
  let plainTextToUse = plainTextFallback;

  // If we don't have plain text fallback, create it from markdown
  if (!plainTextToUse && markdownText) {
    plainTextToUse = markdownText
      .replace(/#{1,6}\s+/g, "") // Remove markdown headers
      .replace(/\*\*(.*?)\*\*/g, "$1") // Remove bold markdown
      .replace(/\*(.*?)\*/g, "$1") // Remove italic markdown
      .replace(/`(.*?)`/g, "$1") // Remove code markdown
      .replace(/\[([^\]]+)\]\([^)]+\)/g, "$1") // Remove link markdown
      .replace(/^\s*[-*+]\s+/gm, "• ") // Convert list markers to bullets
      .replace(/^\s*\d+\.\s+/gm, "") // Remove numbered list markers
      .trim();
  }

  try {
    // Try to copy as HTML first (for rich text editors)
    if (navigator.clipboard && window.ClipboardItem && htmlToUse && plainTextToUse) {
      const sanitizedHtml = DOMPurify.sanitize(htmlToUse);
      const htmlBlob = new Blob([sanitizedHtml], { type: "text/html" });
      const textBlob = new Blob([plainTextToUse], { type: "text/plain" });

      const clipboardItem = new ClipboardItem({
        "text/html": htmlBlob,
        "text/plain": textBlob,
      });

      await navigator.clipboard.write([clipboardItem]);
      onSuccess?.();
      return true;
    } else {
      // Fallback to plain text
      const textToUse = plainTextToUse || markdownText || "";
      await navigator.clipboard.writeText(textToUse);
      onSuccess?.();
      return true;
    }
  } catch (error) {
    console.error("Failed to copy:", error);

    try {
      // Secondary fallback - try plain text clipboard
      const fallbackText = plainTextToUse || markdownText || "";
      const processedText = fallbackText
        .replace(/#{1,6}\s+/g, "") // Remove markdown headers
        .replace(/\*\*(.*?)\*\*/g, "$1") // Remove bold markdown
        .replace(/\*(.*?)\*/g, "$1") // Remove italic markdown
        .replace(/`(.*?)`/g, "$1") // Remove code markdown
        .replace(/\[([^\]]+)\]\([^)]+\)/g, "$1") // Remove link markdown
        .replace(/^\s*[-*+]\s+/gm, "• ") // Convert list markers to bullets
        .replace(/^\s*\d+\.\s+/gm, "") // Remove numbered list markers
        .trim();

      await navigator.clipboard.writeText(processedText);
      onSuccess?.();
      return true;
    } catch (fallbackError) {
      console.error("All clipboard methods failed:", fallbackError);

      try {
        // Final fallback - old school method with consistent text processing
        const finalText = plainTextToUse || markdownText || "";
        const processedText = finalText
          .replace(/#{1,6}\s+/g, "") // Remove markdown headers
          .replace(/\*\*(.*?)\*\*/g, "$1") // Remove bold markdown
          .replace(/\*(.*?)\*/g, "$1") // Remove italic markdown
          .replace(/`(.*?)`/g, "$1") // Remove code markdown
          .replace(/\[([^\]]+)\]\([^)]+\)/g, "$1") // Remove link markdown
          .replace(/^\s*[-*+]\s+/gm, "• ") // Convert list markers to bullets
          .replace(/^\s*\d+\.\s+/gm, "") // Remove numbered list markers
          .trim();

        const textArea = document.createElement("textarea");
        textArea.value = processedText;
        textArea.style.position = "fixed";
        textArea.style.opacity = "0";
        document.body.appendChild(textArea);
        textArea.select();
        document.execCommand("copy");
        document.body.removeChild(textArea);
        onSuccess?.();
        return true;
      } catch (finalError) {
        onError?.(finalError);
        return false;
      }
    }
  }
};

/**
 * Avoids sending the same link twice: many native share UIs concatenate `text` + `url`,
 * so if both are identical (or `text` is only a URL already covered by `url`), keep a single field.
 */
function normalizeSharePayloadForReactNativeWebView(sharePayload: { title?: string; text?: string; url?: string }): {
  title?: string;
  text?: string;
  url?: string;
} {
  const title = sharePayload.title?.trim() || undefined;
  let text = sharePayload.text?.trim() || undefined;
  let url = sharePayload.url?.trim() || undefined;

  if (text && url && text === url) {
    text = undefined;
  }

  const textLooksLikeOnlyUrl = text && !url && (text.startsWith("http://") || text.startsWith("https://"));

  if (textLooksLikeOnlyUrl) {
    url = text;
    text = undefined;
  }

  return { title, text, url };
}

/**
 * Sends share payload to the native app when the web bundle runs inside a React Native WebView.
 * Returns false when not in that environment or when postMessage fails.
 */
function postNativeShareForReactNativeWebView(sharePayload: { title?: string; text?: string; url?: string }): boolean {
  if (typeof window === "undefined") {
    return false;
  }
  const reactNativeWebView = (window as any).ReactNativeWebView;
  if (typeof reactNativeWebView?.postMessage !== "function") {
    return false;
  }
  const normalizedPayload = normalizeSharePayloadForReactNativeWebView(sharePayload);
  try {
    reactNativeWebView.postMessage(
      JSON.stringify({
        typeEvent: EventSentToMobileApp.SHARE,
        message: JSON.stringify(normalizedPayload),
      })
    );
    return true;
  } catch (error) {
    console.error("Failed to postMessage SHARE event:", error);
    return false;
  }
}

function isRunningInsideReactNativeWebViewWithBridge(): boolean {
  return typeof window !== "undefined" && typeof (window as any).ReactNativeWebView?.postMessage === "function";
}

/**
 * If the string is only the same http(s) URL repeated (whitespace-separated), return a single copy.
 * Defensive: some native share targets still concatenate fields; avoids "url url" in the clipboard.
 */
function collapseWhitespaceSeparatedDuplicateUrls(text: string): string {
  const trimmedText = text.trim();
  const segments = trimmedText.split(/\s+/).filter(Boolean);
  if (segments.length < 2) {
    return trimmedText;
  }
  const firstSegment = segments[0];
  const firstSegmentLooksLikeHttpUrl = firstSegment.startsWith("http://") || firstSegment.startsWith("https://");
  if (!firstSegmentLooksLikeHttpUrl) {
    return trimmedText;
  }
  const allSegmentsMatchFirst = segments.every((segment) => segment === firstSegment);
  return allSegmentsMatchFirst ? firstSegment : trimmedText;
}

function recordAppShare(text: string, method: "copy_link" | "native" | "web_share", requested = false) {
  const share_surface = sharedAppSurface(text, window.location.origin);
  if (share_surface) void trackEvent(requested ? "App Share Requested" : "App Content Shared", { share_surface, method }, null, null);
}

export const copyTextToClipboard = async (text: string): Promise<boolean> => {
  const trimmedText = collapseWhitespaceSeparatedDuplicateUrls((text ?? "").trim());
  if (!trimmedText) {
    return false;
  }
  // React Native WebView: show the native share sheet instead of web clipboard APIs (often unreliable or wrong UX here).
  if (isRunningInsideReactNativeWebViewWithBridge()) {
    const sharePayload =
      trimmedText.startsWith("http://") || trimmedText.startsWith("https://")
        ? { url: trimmedText }
        : { text: trimmedText };
    const requested = postNativeShareForReactNativeWebView(sharePayload);
    if (requested) recordAppShare(trimmedText, "native", true);
    return requested;
  }

  if (pageStore.getState().isRunningAsMobileApp) {
    return false;
  }

  if (typeof navigator !== "undefined" && navigator.clipboard?.writeText) {
    await navigator.clipboard.writeText(trimmedText);
    recordAppShare(trimmedText, "copy_link");
    return true;
  }

  if (typeof document === "undefined" || typeof document.body === "undefined") {
    return false;
  }

  try {
    const textarea = document.createElement("textarea");
    textarea.value = trimmedText;
    textarea.style.position = "fixed";
    textarea.style.opacity = "0";
    textarea.setAttribute("readonly", "");
    document.body.appendChild(textarea);

    textarea.select();
    const copied = document.execCommand("copy");
    document.body.removeChild(textarea);
    if (copied) recordAppShare(trimmedText, "copy_link");
    return copied;
  } catch (err) {
    console.error("Fallback copy failed:", err);
    return false;
  }
};

export type ShareContentInput = {
  title?: string;
  text?: string;
  url?: string;
};

export type ShareContentResultMethod = "native_share" | "web_share" | "clipboard" | "failed";

/**
 * Shares content with the best available mechanism:
 * - Mobile app WebView: triggers native share sheet via Web→Mobile event
 * - Web browsers: uses Web Share API if available
 * - Fallback: copies URL/text to clipboard
 */
export const shareContent = async (input: ShareContentInput): Promise<ShareContentResultMethod> => {
  const title = input?.title?.trim() || undefined;
  const text = input?.text?.trim() || undefined;
  const url = input?.url?.trim() || undefined;

  const payloadToShare = { title, text, url };

  // 1) React Native WebView (mobile app) → request native share UI
  if (postNativeShareForReactNativeWebView(payloadToShare)) {
    recordAppShare(url || text || "", "native", true);
    return "native_share";
  }

  // 2) Web Share API (mobile Safari/Chrome, some desktops)
  try {
    const navigatorAny = navigator as any;
    if (typeof navigatorAny?.share === "function") {
      await navigatorAny.share({ title, text, url });
      recordAppShare(url || text || "", "web_share");
      return "web_share";
    }
  } catch (error) {
    // User cancellation throws in some browsers; we treat it as a non-fatal failure and fall back.
    console.warn("Web share failed:", error);
  }

  // 3) Clipboard fallback (never use web clipboard inside the embedded React Native app shell)
  const textToCopy = url || text || "";
  if (!textToCopy) {
    return "failed";
  }

  const shouldNotUseWebClipboardFallback =
    isRunningInsideReactNativeWebViewWithBridge() || pageStore.getState().isRunningAsMobileApp;

  if (shouldNotUseWebClipboardFallback) {
    return "failed";
  }

  const didCopy = await copyTextToClipboard(textToCopy);
  return didCopy ? "clipboard" : "failed";
};

export const getRelativeTimeToExam = (date: Date | { seconds: number; nanoseconds: number }, t: any) => {
  const examDate = date instanceof Date ? date : new Date(date.seconds * 1000 + date.nanoseconds / 1000000);
  const currentDate = new Date();

  // Set hours, minutes, seconds, and milliseconds to 0 for date comparison as we don't know the exact hour of the user exam
  const examDateStart = new Date(examDate);
  examDateStart.setHours(0, 0, 0, 0);
  const currentDate12am = new Date(currentDate);
  currentDate12am.setHours(0, 0, 0, 0);

  if (examDateStart < currentDate12am) return t("past_exam", "Past exam");
  if (examDateStart.getTime() === currentDate12am.getTime()) return t("today", "today");

  // Calculate the difference in days
  const diffInMs = examDateStart.getTime() - currentDate12am.getTime();
  const diffInDays = Math.ceil(diffInMs / (1000 * 60 * 60 * 24));

  // Handle tomorrow specifically
  if (diffInDays === 1) {
    return t("tomorrow", "tomorrow");
  }

  // Handle days (2-13 days)
  if (diffInDays <= 13) {
    return `${t("in", "in")} ${diffInDays} ${diffInDays === 1 ? t("day", "day") : t("days", "days")}`;
  }

  // Handle weeks (14-59 days, roughly 2-8 weeks)
  if (diffInDays <= 59) {
    const weeks = Math.round(diffInDays / 7);
    return `${t("in", "in")} ${weeks} ${weeks === 1 ? t("week", "week") : t("weeks", "weeks")}`;
  }

  // Handle months (60-364 days, roughly 2-12 months)
  if (diffInDays <= 364) {
    const months = Math.round(diffInDays / 30.44); // Average days per month
    return `${t("in", "in")} ${months} ${months === 1 ? t("month", "month") : t("months", "months")}`;
  }

  // Handle years (365+ days)
  const years = Math.round(diffInDays / 365.25); // Account for leap years
  return `${t("in", "in")} ${years} ${years === 1 ? t("year", "year") : t("years", "years")}`;
};
